import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NoRecordsBanner } from "../../OopsBanner";
import { UserContext } from "../../Definitions/UserContext";
import { AdminServiceData, GetNickNameParams } from "../RenderBusinessOwnerName";
import { GetAdminSearchServices, GetBuseinssesInDistanceForRenters, GetBusinessHero_NickName, GetBusinessLocation, SearchLocations, GetLocationDetailsByPlaceID, VagaroSearchBusiness, PlaceLocationData, SearchListingResponse, SearchListingResult, LocationSearchResult, VagaroSearchPayload, SortType } from "../../Utilities/BusinessData";
import { FacilityCategory } from "../../Definitions/RealTimeAvailabilityTypes";
import InputWithDropdown from "../../../VagaroComponent/Controls/InputWIthDropdown";
import { OpenToMultiLocationPage } from "../RenderBusinessLocation";
import XIcon from "../../Icons/XIcon";
import { getCookie, setCookie, GetCookieName } from "../../Utilities/CookieUtils";
import { GetAliasLink, SetLocationStorage } from "../../Utilities/LinkUtils";
import { GetLocationFromLatLon } from "../../Utilities/LocationAPI";
import DirectoryProCard, { buildBookingUrl } from "./DirectoryProCard";
import DateTimePicker from "./DateTimePicker";
import { Map } from "../../../utils/Map";
import { SuperClusterAlgorithm, MarkerClusterer } from "@googlemaps/markerclusterer";
import { US_STATE_BOUNDS } from "./usStateBounds";
import { utils } from "../../../utils/Utils";
import VGPopUp from "../../Controls/VGPopUp";
import "./RealTimeAvailability.css";
import { decodeAmp } from "../../Utilities/StringUtils";
import { parseSearchPrefill, hasSearchPrefill, buildSearchPrefillQuery, PrefillDescriptor, SearchUrlState } from "./searchPrefill";

/** Search Deep Link params are consumer-only, gated off in the editor unless QA opts
 *  in (window.__RTA_ALLOW_EDITOR_PREFILL = true, or localStorage "rta-enable-editor-prefill" = "1"). */
function isSearchPrefillAllowed(): boolean {
    const windowObj = window as { isEditor?: boolean; __RTA_ALLOW_EDITOR_PREFILL?: boolean };
    const editorOptIn = windowObj.__RTA_ALLOW_EDITOR_PREFILL === true
        || window.localStorage?.getItem("rta-enable-editor-prefill") === "1";
    return !windowObj.isEditor || editorOptIn;
}

/** Reads the Search Deep Link params (ADR 0005) once on mount. */
function readSearchPrefill(layout: string, facilityCategories: FacilityCategory[]): PrefillDescriptor | null {
    if (!isSearchPrefillAllowed()) return null;
    const descriptor = parseSearchPrefill(window.location.search, layout, facilityCategories);
    return hasSearchPrefill(descriptor) ? descriptor : null;
}

/** Mirrors the live search state back into the URL (ADR 0005 §6) so it's shareable;
 *  replaceState (not pushState) keeps search refinements out of Back-button history. */
function writeSearchPrefillToUrl(state: SearchUrlState): void {
    if (!isSearchPrefillAllowed()) return;

    const query = buildSearchPrefillQuery(state, window.location.search);
    const url = window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
    window.history.replaceState(window.history.state, "", url);
}

type FacilityValue = { HID: string; VID: string; TP: string; value: string };

/** Maps selected facility `value` tokens to the VagaroSearchBusiness facilitiesvalues shape. */
function buildFacilitiesValues(categories: FacilityCategory[], selected: string[]): FacilityValue[] {
    const facilitiesvalues: FacilityValue[] = [];
    for (const cat of categories) {
        for (const sub of cat.SubFacilities) {
            if (!selected.includes(sub.value)) continue;
            facilitiesvalues.push({ HID: cat.id, VID: sub.id, TP: sub.header, value: sub.value });
        }
    }
    return facilitiesvalues;
}

/** Keeps a component mounted for `delay` ms after `open` goes false,
 *  allowing CSS exit transitions to play before React unmounts the DOM. */
function useDelayedUnmount(open: boolean, delay = 300) {
    const [mounted, setMounted] = useState(open);
    useEffect(() => {
        if (open) { setMounted(true); return; }
        const id = window.setTimeout(() => setMounted(false), delay);
        return () => window.clearTimeout(id);
    }, [open, delay]);
    return mounted;
}

/** Minimal Google Maps type declarations — loaded dynamically via Map.loadGoogleMapScript */
interface GoogleMapsPoint { }
interface GoogleMapsLatLng { lat(): number; lng(): number; }
interface GoogleMapsLatLngBounds { extend(point: GoogleMapsLatLng): void; contains(point: GoogleMapsLatLng): boolean; getNorthEast(): GoogleMapsLatLng; getSouthWest(): GoogleMapsLatLng; }
interface GoogleMapsMarker { setMap(map: GoogleMapsMap | null): void; setIcon(icon: MarkerIconDef): void; }
interface GoogleMapsMap {
    fitBounds(bounds: GoogleMapsLatLngBounds, padding?: number): void;
    panTo(latLng: GoogleMapsLatLng | { lat: number; lng: number }): void;
    setZoom(zoom: number): void;
    getZoom?(): number;
    getBounds?(): GoogleMapsLatLngBounds | null;
    getCenter?(): GoogleMapsLatLng | null;
    setOptions?(opts: Record<string, unknown>): void;
}
interface GoogleMapsListener { remove(): void; }
interface GoogleMapsEventNS { addListener(instance: GoogleMapsMarker | GoogleMapsMap, event: string, handler: () => void): GoogleMapsListener; trigger(instance: GoogleMapsMap, event: string): void; }
interface GoogleMapsNS {
    Map: new (el: HTMLElement, opts: Record<string, unknown>) => GoogleMapsMap;
    Marker: new (opts: Record<string, unknown>) => GoogleMapsMarker;
    LatLng: new (lat: number, lng: number) => GoogleMapsLatLng;
    LatLngBounds: new () => GoogleMapsLatLngBounds;
    Point: new (x: number, y: number) => GoogleMapsPoint;
    Size: new (w: number, h: number) => unknown;
    MapTypeId: { ROADMAP: string };
    event: GoogleMapsEventNS;
}
interface WindowWithGoogle extends Window { google?: { maps?: GoogleMapsNS }; isEditor?: boolean; }
interface MarkerIconDef { path: string; fillColor: string; fillOpacity: number; strokeColor: string; strokeWeight: number; scale: number; anchor: unknown; labelOrigin: unknown; }
interface MarkerWithClickState extends GoogleMapsMarker { __isClicked?: boolean; }
interface MapSearchCenter {
    lat: number;
    lon: number;
    /** Two-letter state/province code from the geocoder, used to look up a hardcoded
     *  bounding box when the user searched a region (e.g. "Texas") rather than a city. */
    stateCode?: string;
    /** True when the geocoder matched a region/state with no specific city — signals
     *  that fitBounds should use the state bounding box instead of nearest-N markers. */
    isStateLevelSearch?: boolean;
}


export const directoryPageSize = 15;
/** Larger page size for the "locations" view (no service search) — need enough results
 *  to cover many Sola properties since each property may have 20+ individual tenants. */
export const locationViewPageSize = 100;

/** Shape of a single location from the mysite/distance API (GetBuseinssesInDistanceForRenters).
 *  This is the old Vagaro internal format — not the same as NearbyLocationData (MongoDB). */
interface LocationDistanceResult {
    encBID: string;
    businessName: string;
    latitude: number;
    longitude: number;
    distance: number;
    street: string;
    streetNo: string;
    city: string;
    stateCode: string;
    zip: string;
    alias: string;
    nickName?: string;
}

const cardStencilData: SearchListingResult = {
    merchantId: "", ownerName: "", ownerPhoto: "", businessName: "", description: "",
    businessImagePath: "", facilityValue: "", vagaroURL: "", city: "", stateName: "",
    reviewTotal: 0, averageRank: 0, serviceProviderID: 0, freeTime: "", appointmentDate: "",
    FromTime: "", serviceID: 0, encServiceId: "", price: 0, isShowPriceOnline: false,
    latitude: 0, longitude: 0, distance: "", telephone: "", stateCode: "", strDistance: "",
    strFreeTime: "", sGroup: "", serviceTitle: "", eventType: 0, redirectURL: "",
    redirectAboutPageURL: "", stateCity: "", street: "", zip: "", isdormant: true,
};
const cardStencilArr = [cardStencilData, cardStencilData, cardStencilData];

const isStrippedChar = (ch: string) => ch === "'" || ch === "\u2019" || ch === "`";
const normalizeSearch = (str: string) => str.replace(/['\u2019`]/g, "");
const buildIndexMap = (original: string): number[] => {
    const map: number[] = [];
    for (let i = 0; i < original.length; i++) {
        if (!isStrippedChar(original[i])) map.push(i);
    }
    map.push(original.length);
    return map;
};

const getFilteredServiceCatList = (data: AdminServiceData[], text: string) => {
    if (!text) return [];
    const terms = normalizeSearch(text.toLowerCase()).split(" ");
    const filteredData: { category: string; service: string[]; nodes: ReactNode[] }[] = [];

    for (let i = 0; i < data.length; i++) {
        const item: { category: string; service: string[]; nodes: ReactNode[] } = { category: data[i].category, service: [], nodes: [] };
        for (let e = 0; e < data[i].service.length; e++) {
            const serviceName = data[i].service[e]?.text ?? "";
            const serviceLC = normalizeSearch(serviceName.toLowerCase());
            let isMatch = true;
            for (let j = 0; j < terms.length; j++) {
                if (!serviceLC.includes(terms[j])) { isMatch = false; break; }
            }
            if (isMatch) {
                if (terms.length === 1) {
                    const str = terms[0];
                    const index = serviceLC.indexOf(str);
                    const indexMap = buildIndexMap(serviceName.toLowerCase());
                    const origStart = indexMap[index];
                    const origEnd = indexMap[index + str.length];
                    item.nodes.push(<div className="inset-item"><span>{serviceName.substring(0, origStart)}<b>{serviceName.substring(origStart, origEnd)}</b>{serviceName.substring(origEnd)}</span></div>);
                } else {
                    item.nodes.push(<div className="inset-item"><span>{serviceName}</span></div>);
                }
                item.service.push(serviceName);
            }
        }
        if (item.service.length > 0) filteredData.push(item);
    }
    return filteredData;
};

export default function RenderRealTimeAvailability({ data, layout, siteData, bTypes, hideServices, splitTypes, placeholder1, placeholder2, placeholder3, enterpriseType, facilityFilters, encryptedPromoCode, proNameSrc, proPhotoSrc, linkPage, linkPage2, linkTarget, clusterRadius = 40 }: {
    placeholder1?: string; placeholder2?: string; placeholder3?: string;
    hideServices: boolean; data: any; layout: string; siteData: UserContext; bTypes: any; splitTypes?: boolean; enterpriseType?: string;
    facilityFilters?: FacilityCategory[];
    encryptedPromoCode?: string;
    proNameSrc?: string; proPhotoSrc?: string;
    linkPage?: string; linkPage2?: string; linkTarget?: string;
    clusterRadius?: number;
}) {
    const [searchText, setRootSearchText] = useState("");
    const searchTextRef = useRef("");
    const setSearchText = (text: string) => { searchTextRef.current = text; setRootSearchText(text); };

    const firstResultListRef = useRef<SearchListingResult[]>([]);

    const lastServiceRef = useRef("");
    const [lastService, setLastService] = useState("");
    const [serviceData, setServiceData] = useState<AdminServiceData[]>([]);

    const [serviceText, setRootServiceText] = useState("");
    const serviceTextRef = useRef("");
    const setServiceText = (text: string) => { serviceTextRef.current = text; setRootServiceText(text); };

    const [address, setAddress] = useState("");

    // Date/time picker state — remembered across hide/show via refs
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const savedDateRef = useRef<Date | null>(null);
    const savedTimeRef = useRef<string | null>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerMounted = useDelayedUnmount(pickerOpen);

    // Filter drawer state
    const [activeBTypeFilter, setActiveBTypeFilter] = useState<string[]>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const filterCategories = useMemo(() => {
        if (!facilityFilters) return [];
        const categoryOrder: Record<string, number> = {
            "Beauty": 0, "Wellness": 1, "Fitness": 2, "Other": 3, "Amenities": 4
        };
        return [...facilityFilters].sort((a, b) => {
            const oa = categoryOrder[a.categoryName] ?? 50;
            const ob = categoryOrder[b.categoryName] ?? 50;
            return oa !== ob ? oa - ob : a.categoryName.localeCompare(b.categoryName);
        });
    }, [facilityFilters]);

    // --- Cached enterprise business list (fetched once, passed to LocationSearchInput) ---
    const [cachedBusinessList, setCachedBusinessList] = useState<any[] | null>(null);
    useEffect(() => {
        if (layout !== "2") return;
        GetBusinessLocation(siteData, true, (data: any) => {
            if (data) {
                setCachedBusinessList(data.map((d: any) => {
                    d.all = d.businessName + " " + d.street + " " + d.streetNo + " " + d.city + " " + d.stateCode + " " + d.stateName + " " + d.zip + " " + d.distance + " " + d.alias + " " + d.nickName;
                    return d;
                }));
            } else {
                setCachedBusinessList([]);
            }
        }, () => { setCachedBusinessList([]); });
    }, [layout, siteData]);

    // --- All-locations search state (layout "2") ---
    const [googleData, setGoogleData] = useState<PlaceLocationData | null>(null);
    const cachedElasticServiceIds = useRef<string>("");
    const configuredPromo = (encryptedPromoCode || "").trim();
    const cachedPromo = useRef<string>(configuredPromo);
    useEffect(() => { cachedPromo.current = configuredPromo; }, [configuredPromo]);
    const cachedPagingDetails = useRef<string>("");
    const [crossLocResults, setDirectoryResults] = useState<SearchListingResponse | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [isNewSearch, setIsNewSearch] = useState(false);
    
    // Add searchRef to track search requests and discard stale responses 
    const searchRequestIdRef = useRef<number>(0);
    const crossLocPage = useRef<number>(1);
    let groupID = localStorage.getItem("Group");
    if (!utils.Validate(groupID)) {
        groupID = "US02";
    }
    const initialSearchFired = useRef(false);
    const [sortMode, setSortMode] = useState<SortMode>("nearest");



    // Filter services by country: include common (0) + current location's country
    const countryFilteredServices = useMemo(() => {
        const countryID = googleData?.countryID ?? siteData.countryCode ?? 1;
        return serviceData
            .filter(cat => cat.countryID === 0 || cat.countryID === countryID)
            .map(cat => ({
                ...cat,
                service: cat.service.filter(s => s.countryID === 0 || s.countryID === countryID),
            }))
            .filter(cat => cat.service.length > 0);
    }, [serviceData, googleData?.countryID, siteData.countryCode]);

    // --- Location-first state (layout "2"): mysite/distance → location list → drill into pros ---
    const [locationResults, setLocationResults] = useState<LocationDistanceResult[] | null>(null);
    const [isLoadingLocations, setIsLoadingLocations] = useState(layout === "2");

    const mapPanelMounted = useRef(false);
    const [userSearchedLocation, setUserSearchedLocation] = useState(false);
    const [showLocationFallbackPopup, setShowLocationFallbackPopup] = useState(false);

    // Resolved user location (lifted from LocationSearchInput for mount-time search)
    const resolvedLat = useRef(SF_DEFAULT_LAT);
    const resolvedLon = useRef(SF_DEFAULT_LON);
    const resolvedCountryID = useRef<number | string|null>(null);
    const [locationUnavailable, setLocationUnavailable] = useState(false);

    // Layout 1: gate user-initiated searches until GetBusinessHero_NickName resolves.
    const locationResolvedRef = useRef(layout === "2"); // Layout 2 resolves location separately
    const pendingSearchRef = useRef<(() => void) | null>(null);

    // --- Layout 1 server-search state (-location via VagaroSearchBusiness) ---
    const [singleLocResults, setSingleLocResults] = useState<SearchListingResult[] | null>(null);
    const singleLocAllPros = useRef<SearchListingResult[]>([]); // mount data — all pros at location
    const singleLocPage = useRef<number>(1);
    const singleLocTotalPages = useRef<number>(1);
    const singleLocSlug = useRef<string>(layout !== "2"
        ? (GetNickNameParams() || siteData.nickname || "")
        : "");

    // Toast
    const [toastVisible, setToastVisible] = useState(false);
    const toastTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const showComingSoonToast = () => {
        setToastVisible(true);
        if (toastTimeout.current) clearTimeout(toastTimeout.current);
        toastTimeout.current = setTimeout(() => setToastVisible(false), 3000);
    };

    // Seeds the visible search fields from a Search Deep Link so the user can see
    // and refine what was applied. The mount search itself reads these via refs/overrides.
    const seedPrefillSearchFields = (prefill: PrefillDescriptor) => {
        if (prefill.serviceText) {
            setServiceText(prefill.serviceText);
            lastServiceRef.current = prefill.serviceText;
            setLastService(prefill.serviceText);
        }
        if (prefill.filterValues.length > 0) {
            setActiveBTypeFilter(prefill.filterValues);
        }
        if (prefill.date || prefill.time) {
            setSelectedDate(prefill.date ?? null);
            setSelectedTime(prefill.time ?? null);
            savedDateRef.current = prefill.date ?? null;
            savedTimeRef.current = prefill.time ?? null;
        }
    };

    // --- Layout 1 mount: fire initial single-location search ---
    // Fetches real lat/long from GetBusinessHero_NickName API (mysite/nickname)
    // so the search uses the business's actual coordinates instead of SF defaults.
    const singleLocInitialFired = useRef(false);
    useEffect(() => {
        if (layout === "2" || singleLocInitialFired.current) return;

        const prefill = readSearchPrefill(layout, filterCategories);
        // A filters deep link waits for the async facility-filter fetch (undefined =
        // still loading, null = failed) so tokens validate before the one mount search.
        if (prefill?.hasFilterParam && facilityFilters === undefined) return;
        singleLocInitialFired.current = true;

        if (prefill) seedPrefillSearchFields(prefill);
        // Explicit override — the mount effect's executeSearch closure predates the seeding
        const prefillFacilities = prefill && prefill.filterValues.length > 0
            ? buildFacilitiesValues(filterCategories, prefill.filterValues)
            : undefined;
        const prefillDateTime = prefill && (prefill.date || prefill.time)
            ? { date: prefill.date ?? null, time: prefill.time ?? null }
            : undefined;

        GetBusinessHero_NickName(siteData, (data: any) => {
            const coords = data?.location?.coordinates;
            if(data?.countryId){
                resolvedCountryID.current = data.countryId;
            }

            if (coords && coords.length >= 2) {
                // GeoJSON order: [longitude, latitude]
                const lat = coords[1];
                const lon = coords[0];
                resolvedLat.current = lat;
                resolvedLon.current = lon;
                // Populate URLSlug from nickname API if not set from URL params
                if (!singleLocSlug.current && data?.urlSlug) {
                    singleLocSlug.current = data.urlSlug;
                }
                locationResolvedRef.current = true;
                executeSearch(1, false, { lat, lon }, prefillFacilities, prefillDateTime, undefined, true);

                // Flush any search the user triggered while location was resolving
                if (pendingSearchRef.current) {
                    pendingSearchRef.current();
                    pendingSearchRef.current = null;
                }
            } else {
                setLocationUnavailable(true);
            }
        }, () => {
            setLocationUnavailable(true);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layout, facilityFilters]);

    // --- Shared search executor (both layouts use VagaroSearchBusiness) ---
    // Layout 1 (singleLoc): scoped by URLSlug from the page URL
    // Layout 2 (crossLoc): scoped by lat/lon from location search
    const executeSearch = useCallback((
        pageIndex: number = 1,
        append: boolean = false,
        locationOverride?: { lat: number; lon: number; cityName?: string; stateCode?: string; countryID?: number | string },
        facilitiesOverride?: { HID: string; VID: string; TP: string; value: string; }[],
        dateTimeOverride?: { date: Date | null; time: string | null },
        sortModeOverride?: SortMode,
        // The mount/prefill search opts out of URL write-back so a pasted deep link is
        // preserved verbatim until the user refines it (ADR 0005 §6).
        isMountSearch: boolean = false
    ) => {

        if (layout !== "2" && !locationResolvedRef.current && !locationOverride) {
            pendingSearchRef.current = () => executeSearch(pageIndex, append, undefined, facilitiesOverride, dateTimeOverride, sortModeOverride, isMountSearch);
            return;
        }

        if (pageIndex === 1) {
            // Clear cached elastic IDs on any fresh search — service text change,
            // location change (redo-search), or filter change all need fresh ES results.
            // The cache only benefits page 2+ pagination within the same search context.
            cachedElasticServiceIds.current = "";
            cachedPagingDetails.current = "";
            if (layout !== "2") {
                singleLocPage.current = 1;
                singleLocTotalPages.current = 1;
            }
        }

        // Location: explicit override → googleData (layout 2) → resolved defaults
        const lat = locationOverride?.lat ?? googleData?.latitude ?? resolvedLat.current;
        const lon = locationOverride?.lon ?? googleData?.longitude ?? resolvedLon.current;
        const latlong = `${lat}|${lon}`;
        const cityName = locationOverride?.cityName ?? googleData?.cityState ?? "";
        const stateCode = locationOverride?.stateCode ?? googleData?.stateCode ?? "";
        let countryID = locationOverride?.countryID ?? googleData?.countryID ?? siteData.countryCode ?? 1;

        if(layout === "1" && resolvedCountryID.current) {
                countryID = resolvedCountryID.current;
         }           // Layout 1: fetch all pros at location in one request for client-side filtering
        // Layout 2: larger page for location view, standard for service search
        const singleLocPageSize = 250;
        const isLocationView = layout === "2" && serviceTextRef.current.length === 0;
        const recordsPerPage = layout !== "2" ? singleLocPageSize : (isLocationView ? locationViewPageSize : directoryPageSize);

        const payload: VagaroSearchPayload = {
            type: "all",
            sortType: sortModeToSortType(sortModeOverride ?? sortMode),
            latlong,
            countryID,
            searchFilter: 2,
            Radius: 50,
            arrSearchFilter: "0,2",
            cityName,
            stateCode,
            sPagingDetails: pageIndex === 1
                ? ""
                : cachedPagingDetails.current || `[{"sGroup":"${groupID}","skipRecords":${(pageIndex - 1) * recordsPerPage},"totalRecords":${recordsPerPage}}]`,
            promo: cachedPromo.current || configuredPromo,
        };

        // Layout 1: scope to single location via URL slug
        if (layout !== "2" && singleLocSlug.current) {
            payload.URLSlug = singleLocSlug.current;
        }

        // Facility filters — priority: explicit override (even empty []) → active filter state
        // undefined = no override (use activeBTypeFilter); [] = explicitly no filters
        if (filterCategories.length > 0) {
            let facilitiesvalues: FacilityValue[] | undefined = facilitiesOverride;

            if (facilitiesvalues === undefined && activeBTypeFilter.length > 0) {
                facilitiesvalues = buildFacilitiesValues(filterCategories, activeBTypeFilter);
            }

            if (facilitiesvalues && facilitiesvalues.length > 0) {
                payload.facilitiesvalues = facilitiesvalues;
            }
        }

        // Send cached elasticService IDs when the service hasn't changed
        if (cachedElasticServiceIds.current.length > 0) {
            payload.vagaroServiceIDs = cachedElasticServiceIds.current;
        }

        // Search text — service takes priority, fall back to name search
        if (serviceTextRef.current.length > 0) {
            payload.searchText = serviceTextRef.current;
        } else if (searchTextRef.current.length > 0) {
            payload.searchText = searchTextRef.current;
        }


        // Date/time — priority: explicit override → state
        const resolvedDate = dateTimeOverride ? dateTimeOverride.date : selectedDate;
        const resolvedTime = dateTimeOverride ? dateTimeOverride.time : selectedTime;
        const effectiveDate = resolvedDate ?? new Date();
        const yyyy = effectiveDate.getFullYear();
        const mm = String(effectiveDate.getMonth() + 1).padStart(2, "0");
        const dd = String(effectiveDate.getDate()).padStart(2, "0");
        payload.serviceDate = `${yyyy}-${mm}-${dd}`;
        if (resolvedTime) {
            const [h, m] = resolvedTime.split(":");
            const hour = parseInt(h);
            const ampm = hour < 12 ? "AM" : "PM";
            payload.serviceTime = `${(hour % 12 || 12).toString().padStart(2, "0")}:${m} ${ampm}`;
        }

        // Write-back: mirror this committed search into the URL. Skipped for
        // pagination and the mount search; derived from the effective values above, not React state.
        if (!append && !isMountSearch) {
            writeSearchPrefillToUrl({
                layout,
                serviceText: serviceTextRef.current || undefined,
                // Only an explicit place (carried on the override) rewrites the URL location; a
                // coordinate-only search passes no cityName, so the last picked place is preserved.
                locationLabel: locationOverride?.cityName,
                filterValues: facilitiesOverride ? facilitiesOverride.map(f => f.value) : activeBTypeFilter,
                date: resolvedDate,
                time: resolvedTime,
            });
        }

        setIsNewSearch(!append);
        setIsSearching(true);

        // Layout 1: clear results on new search so stencil cards show during loading
        if (!append && layout !== "2") {
            setSingleLocResults(null);
        }

        const requestId = ++searchRequestIdRef.current;

        VagaroSearchBusiness(siteData, payload, pageIndex, recordsPerPage,
            (data: SearchListingResponse) => {
                if (requestId !== searchRequestIdRef.current) return;
                if (data) {
                    if (pageIndex === 1 && data.elasticService) {
                        cachedElasticServiceIds.current = data.elasticService;
                    }
                    if (data.promo) {
                        cachedPromo.current = data.promo;
                    }
                    if (data.sPagingDetails) {
                        cachedPagingDetails.current = data.sPagingDetails;
                    }
                    if (firstResultListRef.current.length === 0 && data?.response && data.response.length > 0) {
                        firstResultListRef.current = [...data.response];
                    }

                    if (layout === "2") {
                        const pageResults = data.response || [];
                        if (append) {
                            setDirectoryResults(prev => {
                                if (!prev?.response) return prev ?? data;
                                const merged = { ...data, response: [...prev.response, ...pageResults] };
                                return merged;
                            });
                        } else {
                            setDirectoryResults(data);
                        }
                    } else {
                        // Single-loc: append or replace
                        // Backend totalPage is unscoped — compute from actual result count instead
                        const results = data.response || [];
                        const hasMoreResults = results.length >= singleLocPageSize;
                        singleLocTotalPages.current = hasMoreResults ? pageIndex + 1 : pageIndex;
                        // Cache mount/default results (no service text) for client-side merging
                        if (!serviceTextRef.current && pageIndex === 1) {
                            singleLocAllPros.current = results;
                        }
                        if (append) {
                            setSingleLocResults(prev => prev ? [...prev, ...results] : results);
                            // Also append to all-pros cache
                            if (!serviceTextRef.current) {
                                singleLocAllPros.current = [...singleLocAllPros.current, ...results];
                            }
                        } else {
                            setSingleLocResults(results);
                        }
                    }
                }
                setIsSearching(false);
                setIsNewSearch(false);
            },
            () => {
                if (requestId !== searchRequestIdRef.current) return;
                if (layout !== "2") setSingleLocResults([]);
                setIsSearching(false);
                setIsNewSearch(false);
            }
        );

        if (layout === "2") crossLocPage.current = pageIndex;
        else singleLocPage.current = pageIndex;
    }, [layout, googleData, siteData, selectedDate, selectedTime, groupID, activeBTypeFilter, filterCategories, sortMode, configuredPromo]);

    const loadMoreCrossLoc = useCallback(() => {
        executeSearch(crossLocPage.current + 1, true);
    }, [executeSearch]);

    const loadMoreSingleLoc = useCallback(() => {
        executeSearch(singleLocPage.current + 1, true);
    }, [executeSearch]);

    const handleSortChange = useCallback((mode: SortMode) => {
        setSortMode(mode);
        executeSearch(1, false, undefined, undefined, undefined, mode);
    }, [executeSearch]);

    // Reset to "nearest" if current sort requires date/time and user switches to Anytime
    const hasDateTimeSelected = selectedDate !== null && selectedTime !== null;
    useEffect(() => {
        if (!hasDateTimeSelected && (sortMode === "highest-cost" || sortMode === "lowest-cost" || sortMode === "ending-soon" || sortMode === "earliest")) {
            setSortMode("nearest");
        }
    }, [hasDateTimeSelected, sortMode]);

    // Coords at which locationResults was last successfully fetched.
    const locationResultsCoordsRef = useRef<{ lat: number; lon: number } | null>(null);

   // Lets Current Location restore the user-home dataset even moving map
    const homeBaselineRef = useRef<{ lat: number; lon: number; data: LocationDistanceResult[] } | null>(null);

    // Set to true when the user explicitly navigates (Current Location) to force a map refit
    //  Only the Current Location button is allowed to set this
    const pendingCurrentLocationRefit = useRef(false);

    // --- Fetch enterprise locations via mysite/distance (layout "2" initial view) ---
    const fetchLocations = useCallback((lat: number, lon: number, establishBaseline: boolean = false) => {
        if (layout !== "2") return;
        setIsLoadingLocations(true);

        GetBuseinssesInDistanceForRenters(siteData, lat, lon,
            (data: LocationDistanceResult[]) => {
                if (data && Array.isArray(data)) {
                    setLocationResults(data);
                    locationResultsCoordsRef.current = { lat, lon };
                    if (establishBaseline) {
                        homeBaselineRef.current = { lat, lon, data };
                    }
                } else {
                    setLocationResults([]);
                    locationResultsCoordsRef.current = null;
                }
                setIsLoadingLocations(false);
            },
            () => {
                console.error("[RTA] mysite/distance fetch failed");
                setLocationResults([]);
                locationResultsCoordsRef.current = null;
                setIsLoadingLocations(false);
            }
        );
    }, [layout, siteData]);

    const allowEditorLocationRedirect = useCallback(() => {
        const windowObj = window as any;
        if (!windowObj.isEditor) return true;
        return windowObj.__RTA_ALLOW_EDITOR_LOCATION_REDIRECT === true
            || window.localStorage?.getItem("rta-enable-editor-location-redirect") === "1";
    }, []);

    const normalizedLinkTarget = linkTarget === "_self" ? "_self" : "_blank";

    const getLocationPageHref = useCallback((alias: string) => {
        const normalizedAlias = alias.trim().replace(/^\/+|\/+$/g, "");
        if (!normalizedAlias) return "#";
        if (!allowEditorLocationRedirect()) return "#";

        return GetAliasLink(normalizedAlias, linkPage2 || "location", siteData, false);
    }, [siteData, allowEditorLocationRedirect, linkPage2]);

    const getProPageHref = useCallback((mappingURL: string) => {
        const normalizedAlias = (mappingURL || "").trim().replace(/^\/+|\/+$/g, "");
        if (!normalizedAlias) return "#";
        const selectedPage = linkPage || "pro";
        return GetAliasLink(normalizedAlias, selectedPage, siteData, false);
    }, [siteData, linkPage]);

    const handleLocationCardClick = useCallback((location: LocationGroup) => {
        if (!location.alias) return;
        const windowObj = window as any;

        // Default behavior: no link-out in editor.
        // Temporary override for QA/dev in WebsiteBuilder:
        // - window.__RTA_ALLOW_EDITOR_LOCATION_REDIRECT = true
        // - localStorage.setItem("rta-enable-editor-location-redirect", "1")
        if (windowObj.isEditor && !allowEditorLocationRedirect()) return;

        if (windowObj.isEditor) {
            const href = getLocationPageHref(location.alias);
            if (href !== "#") {
                window.open(href, normalizedLinkTarget);
            }
            return;
        }

        const href = getLocationPageHref(location.alias);
        if (href !== "#") {
            if (normalizedLinkTarget === "_self") {
                OpenToMultiLocationPage("", location.encBID || "", "", location.alias, linkPage2 || "location", siteData, false);
                return;
            }

            const matchingLocation = (siteData.MultilocationData || []).find((loc: any) => loc.encBusinessID === (location.encBID || ""));
            const locationBidHash = matchingLocation?.encBIDHash || "";
            SetLocationStorage("", location.encBID || "", locationBidHash);
            window.open(href, "_blank");
            return;
        }

        OpenToMultiLocationPage("", location.encBID || "", "", location.alias, linkPage2 || "location", siteData, false);
    }, [siteData, allowEditorLocationRedirect, getLocationPageHref, normalizedLinkTarget, linkPage2]);


    const locationGroupsMapped = useMemo((): LocationGroup[] | undefined => {
        if (!locationResults || locationResults.length === 0) return undefined;
        return locationResults.map(loc => ({
            locationName: loc.nickName || loc.businessName,
            street: (loc.street || "") + (loc.streetNo ? " " + loc.streetNo : ""),
            stateCity: [loc.city, loc.stateCode].filter(Boolean).join(", "),
            zip: loc.zip || "",
            latitude: loc.latitude,
            longitude: loc.longitude,
            strDistance: loc.distance != null ? Math.max(1, Math.round(loc.distance)) + " mi" : "",
            businessCount: 0,
            alias: loc.alias,
            encBID: loc.encBID,
        }));
    }, [locationResults]);

    // --- Layout "2" mount: resolve location (cookie → geolocation → SF default), then fetch locations ---
    // Uses mysite/distance instead of VagaroSearchBusiness for the initial location list.
    useEffect(() => {
        if (layout !== "2" || initialSearchFired.current) return;

        const prefill = readSearchPrefill(layout, filterCategories);
        // A filters deep link waits for the async facility-filter fetch (undefined =
        // still loading, null = failed) so tokens validate before the one mount search.
        if (prefill?.hasFilterParam && facilityFilters === undefined) return;
        initialSearchFired.current = true;

        if (prefill) seedPrefillSearchFields(prefill);
        // A deep link with a service or facility filters fires the pro search once the
        // location resolves, folded into the mount flow so exactly one search runs.
        const prefillFacilities = prefill && prefill.filterValues.length > 0
            ? buildFacilitiesValues(filterCategories, prefill.filterValues)
            : undefined;
        const prefillDateTime = prefill && (prefill.date || prefill.time)
            ? { date: prefill.date ?? null, time: prefill.time ?? null }
            : undefined;
        const hasProSearchIntent = !!prefill?.serviceText || !!prefillFacilities;

        const fireInitialLocationFetch = (
            lat: number, lon: number, isUserAuthoritative: boolean,
            locationDetails?: { cityName?: string; stateCode?: string; countryID?: number | string }
        ) => {
            resolvedLat.current = lat;
            resolvedLon.current = lon;
            fetchLocations(lat, lon, isUserAuthoritative);
            if (hasProSearchIntent) {
                executeSearch(1, false, { lat, lon, ...locationDetails }, prefillFacilities, prefillDateTime, undefined, true);
            }
        };

        const resolveFromLocalChain = () => {
            // 1. Check cookie
            const cached = getRTALocationCookie();
            if (cached && cached.lat !== 0 && cached.long !== 0) {
                setUserSearchedLocation(true);
                if (cached.city) {
                    setAddress(cached.city);
                } else {
                    // Cookie exists but city is empty/placeholder — reverse geocode to resolve it
                    GetLocationFromLatLon(siteData, cached.lat.toString(), cached.long.toString(),
                        (data: any) => {
                            const result = data?.data?.data;
                            if (result?.city) {
                                const city = [result.city, result.stateCode].filter(Boolean).join(", ");
                                setAddress(city);
                                cached.city = city;
                                cached.state = result.stateCode ?? "";
                                setRTALocationCookie(cached);
                            }
                        },
                        () => {}
                    );
                }
                setGoogleData({
                    latitude: cached.lat,
                    longitude: cached.long,
                    countryID: cached.countryid ?? 1,
                    cityState: cached.city ?? "",
                    stateCode: cached.state ?? "",
                } as PlaceLocationData);
                fireInitialLocationFetch(cached.lat, cached.long, true, {
                    cityName: cached.city ?? "",
                    stateCode: cached.state ?? "",
                    countryID: cached.countryid ?? 1,
                });
                return;
            }

            // 2. No cookie — use geolocation + reverse geocode for city name
            if ("geolocation" in navigator) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const lat = position.coords.latitude;
                        const lon = position.coords.longitude;
                        setUserSearchedLocation(true);
                            // Reverse geocode to get actual city name
                        GetLocationFromLatLon(siteData, lat.toString(), lon.toString(),
                            (data: any) => {
                                const result = data?.data?.data;
                                if (result?.city) {
                                    const city = [result.city, result.stateCode].filter(Boolean).join(", ");
                                    setAddress(city);
                                    setGoogleData({
                                        latitude: lat, longitude: lon,
                                        cityState: city, stateCode: result.stateCode ?? "",
                                        countryID: result.countryID ?? 1,
                                    } as PlaceLocationData);
                                    setRTALocationCookie(createRTALocationCookie({
                                        lat, long: lon, city, state: result.stateCode ?? "",
                                        countryid: result.countryID ?? null,
                                        currencysymbol: result.currencySymbol ?? "",
                                        stateName: result.state ?? "",
                                        utcOffset: result.utcOffset ?? null,
                                        timeZoneOffSet: result.timeZoneOffSet ?? null,
                                        isSupportDayLight: result.isSupportDayLight ?? null,
                                    }));
                                } else {
                                    setRTALocationCookie(createRTALocationCookie({ lat, long: lon, city: "", state: "" }));
                                }
                            },
                            () => {
                                setRTALocationCookie(createRTALocationCookie({ lat, long: lon, city: "", state: "" }));
                            }
                        );
                        fireInitialLocationFetch(lat, lon, true);
                    },
                    () => {
                        // 3. Denied/error — fall back to SF default
                        fireInitialLocationFetch(SF_DEFAULT_LAT, SF_DEFAULT_LON, false);
                    }
                );
            } else {
                fireInitialLocationFetch(SF_DEFAULT_LAT, SF_DEFAULT_LON, false);
            }
        };

        // 0. Search Deep Link location — an explicit param outranks the cookie and
        // geolocation; any resolution failure falls through to the local chain.
        if (prefill?.location) {
            if ("lat" in prefill.location) {
                // Coordinate fast-path: search on the coords directly; reverse geocode
                // only fills the display label (mirrors the geolocation branch).
                const { lat, lng } = prefill.location;
                setUserSearchedLocation(true);
                GetLocationFromLatLon(siteData, lat.toString(), lng.toString(),
                    (data: any) => {
                        const result = data?.data?.data;
                        if (result?.city) {
                            const city = [result.city, result.stateCode].filter(Boolean).join(", ");
                            setAddress(city);
                            setGoogleData({
                                latitude: lat, longitude: lng,
                                cityState: city, stateCode: result.stateCode ?? "",
                                countryID: result.countryID ?? 1,
                            } as PlaceLocationData);
                        }
                    },
                    () => {}
                );
                fireInitialLocationFetch(lat, lng, true);
            } else {
                // Location string: resolve through the manual-selection pipeline —
                // top autocomplete match, then place details (as handlePlaceSelect).
                const { query } = prefill.location;
                SearchLocations(siteData, query, resolvedLat.current, resolvedLon.current, 1, configuredPromo,
                    (data: LocationSearchResult) => {
                        const top = data?.locations?.[0];
                        if (!top) { resolveFromLocalChain(); return; }
                        GetLocationDetailsByPlaceID(siteData, top.place_id,
                            (place: PlaceLocationData) => {
                                if (!place) { resolveFromLocalChain(); return; }
                                setUserSearchedLocation(true);
                                setAddress(top.CityState);
                                setGoogleData(place);
                                fireInitialLocationFetch(place.latitude, place.longitude, true, {
                                    cityName: place.cityState,
                                    stateCode: place.stateCode,
                                    countryID: place.countryID,
                                });
                            },
                            () => resolveFromLocalChain()
                        );
                    },
                    () => resolveFromLocalChain()
                );
            }
            return;
        }

        resolveFromLocalChain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layout, siteData, fetchLocations, facilityFilters]);

    // Handle location selection from SearchLocations dropdown
    const handlePlaceSelect = useCallback((placeID: string, label: string) => {
        setAddress(label);
        setUserSearchedLocation(true);
        GetLocationDetailsByPlaceID(siteData, placeID,
            (data: PlaceLocationData) => {
                if (data) {
                    setGoogleData(data);

                    resolvedLat.current = data.latitude;
                    resolvedLon.current = data.longitude;

                    // Always fetch locations for the new place — updates map + location card list
                    fetchLocations(data.latitude, data.longitude);

                    if (lastServiceRef.current.length > 0) {
                        executeSearch(1, false, {
                            lat: data.latitude,
                            lon: data.longitude,
                            cityName: data.cityState,
                            stateCode: data.stateCode,
                            countryID: data.countryID,
                        });
                    } else {
                        setDirectoryResults(null);
                        // Location-view pick (no service): the chosen place is the URL's
                        // location; clear any leftover service/filters from a prior search.
                        writeSearchPrefillToUrl({ layout, locationLabel: data.cityState, filterValues: [] });
                    }
                }
            },
            () => { console.error("[RTA] Failed to resolve place:", placeID); }
        );
    }, [siteData, executeSearch, fetchLocations]);

    useEffect(() => {
        if (!hideServices) {
            GetAdminSearchServices(siteData, (data: AdminServiceData[]) => {
                setServiceData(data ?? []);
            }, (err: unknown) => {
                console.error("[RTA] GetAdminSearchServices failed:", err);
            });
        }
    }, [hideServices, siteData]);

    const p1 = placeholder1 && placeholder1.length > 0 ? placeholder1 : "Search by Name";
    const p2 = placeholder2 && placeholder2.length > 0 ? placeholder2 : "Search by Service";
    const p3 = placeholder3 && placeholder3.length > 0 ? placeholder3 : "Search Location";

    const showPicker = lastService.length > 0;
    const hasDateTimeSelection = selectedDate !== null || selectedTime !== null;

    const handlePickerConfirm = (date: Date | null, time: string | null) => {
        // Bug 7: time without date → default to today
        const effectiveDate = time !== null && date === null
            ? new Date(new Date().setHours(0, 0, 0, 0))
            : date;

        setSelectedDate(effectiveDate); setSelectedTime(time);
        savedDateRef.current = effectiveDate; savedTimeRef.current = time;
        setPickerOpen(false);

        if (layout === "2" && lastServiceRef.current.length > 0) {
            executeSearch(1, false, undefined, undefined, { date: effectiveDate, time });
        } else if (layout !== "2" && singleLocResults !== null) {
            executeSearch(1, false, undefined, undefined, { date: effectiveDate, time });
        }
    };

    // Refetches locations if googleData drifted from the last fetch (user dragged + redo'd).
    const revertToLocationView = useCallback(() => {
        setDirectoryResults(null);
        const cachedCoords = locationResultsCoordsRef.current;
        const coordsMatch = cachedCoords
            && cachedCoords.lat === googleData?.latitude
            && cachedCoords.lon === googleData?.longitude;
        if (!coordsMatch && googleData?.latitude && googleData?.longitude) {
            fetchLocations(googleData.latitude, googleData.longitude);
        }
    }, [googleData, fetchLocations]);

    const handleServiceClear = () => {
        const hadService = lastServiceRef.current.length > 0;
        setServiceText("");
        lastServiceRef.current = ""; setLastService("");
        // Only reset results if a service was actually selected
        if (!hadService) return;
        // Reset date/time to "Anytime" — picker hides when service is cleared
        setSelectedDate(null);
        setSelectedTime(null);
        savedDateRef.current = null;
        savedTimeRef.current = null;
        if (layout === "2") {
            // Also clear filters so we fully revert to location view
            setActiveBTypeFilter([]);
            revertToLocationView();
            // Drop q/filters/date/time from the URL but keep the current place
            // (locationLabel omitted → preserved).
            writeSearchPrefillToUrl({ layout, filterValues: [] });
        } else {
            // Layout 1: re-fetch default results (no service filter, no date/time)
            executeSearch(1, false, undefined, undefined, { date: null, time: null });
        }
    };

    const handleServiceRestore = () => {
        // When service has text again, restore remembered selection
        if (savedDateRef.current !== null) setSelectedDate(savedDateRef.current);
        if (savedTimeRef.current !== null) setSelectedTime(savedTimeRef.current);
    };

    const handleApplyFilters = (selected: string[]) => {
        // No-op: filters didn't change — skip unnecessary search
        const filtersChanged = selected.length !== activeBTypeFilter.length
            || selected.some(v => !activeBTypeFilter.includes(v));
        if (!filtersChanged) {
            setDrawerOpen(false);
            return;
        }

        setActiveBTypeFilter(selected);
        setDrawerOpen(false);

        // For layout "2" with active service search, fire search with facility filters
        // (mysite/distance doesn't support facility filters, so only applies to pro search)
        if (layout === "2" && filterCategories.length > 0) {

            if (selected.length === 0 && lastServiceRef.current.length === 0) {
                revertToLocationView();
                // Filters cleared with no service: drop q/filters, keep the current place.
                writeSearchPrefillToUrl({ layout, filterValues: [] });
                return;
            }
            executeSearch(1, false, undefined, buildFacilitiesValues(filterCategories, selected));
        }
        // Layout 1 — re-run with updated facility filters
        // Pass explicit override (even if empty) to avoid stale activeBTypeFilter closure
        else if (layout !== "2") {
            // Empty array override = no filters, avoids stale closure reading old activeBTypeFilter
            executeSearch(1, false, undefined, buildFacilitiesValues(filterCategories, selected));
        }
    };

    const handleClearSearch = useCallback(() => {
        // Reset service
        setServiceText("");
        lastServiceRef.current = "";
        setLastService("");
        // Reset date/time
        setSelectedDate(null);
        setSelectedTime(null);
        savedDateRef.current = null;
        savedTimeRef.current = null;
        // Reset filters
        setActiveBTypeFilter([]);

        if (layout === "2") {
            setDirectoryResults(null);
            setUserSearchedLocation(false);
            // Full reset: wipe every deep-link param, including the location (null → cleared).
            // The restored cookie place is auto-detected, not a user pick, so it stays out of the URL.
            writeSearchPrefillToUrl({ layout, filterValues: [], locationLabel: null });
            // "Clear Search" is full reset-to-home — intentionally discards drag position.
            // Use revertToLocationView for the drag-aware path.
            const cached = getRTALocationCookie();
            if (cached && cached.lat !== 0 && cached.long !== 0) {
                setAddress(cached.city ?? "");
                setGoogleData({
                    latitude: cached.lat,
                    longitude: cached.long,
                    countryID: cached.countryid ?? 1,
                    cityState: cached.city ?? "",
                    stateCode: cached.state ?? "",
                } as PlaceLocationData);
                resolvedLat.current = cached.lat;
                resolvedLon.current = cached.long;
                fetchLocations(cached.lat, cached.long);
            }
        } else {
            // Layout 1: re-fetch default results with no filters.
            // Pass explicit empty arrays/nulls — executeSearch's closure may hold
            // stale state for filters/date since React batches the setState calls above.
            executeSearch(1, false, undefined, [], { date: null, time: null });
        }
    }, [layout, fetchLocations, executeSearch]);

    const formatPickerLabel = () => {
        const parts: string[] = [];
        if (selectedDate) {
            parts.push(selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }));
        }
        if (selectedTime) {
            const [h, m] = selectedTime.split(":");
            const hour = parseInt(h);
            const ampm = hour < 12 ? "AM" : "PM";
            parts.push(`${hour % 12 || 12}:${m} ${ampm}`);
        }
        return parts.length > 0 ? parts.join(" at ") : "Anytime";
    };

    // Once the map panel has mounted, never go back to skeleton — keeps the Google Map alive.
    const showMapSkeleton = !mapPanelMounted.current && !crossLocResults?.response && isLoadingLocations && !locationGroupsMapped?.length;
    if (!showMapSkeleton && layout === "2") mapPanelMounted.current = true;

    return (
        <>
            {(window as any).isEditor && <style>{`.canvas .dir-card-wrapper > *{pointer-events:none!important}`}</style>}
            {/* <div className="select-location-title"><h3 className="">Select location</h3></div> */}
            <div className="dir-search-container">
            <div className="dir-search-row flex-row-to-column">
                <div className="dir-search-bar flex-row-to-column">
                    {/* Layout "1": Name field first. Layout "2": Location field replaces Name */}
                    {layout === "2" ? (
                        <LocationSearchInput
                            businessList={cachedBusinessList}
                            onSelectCurrent={() => {
                                if (typeof navigator !== "undefined" && navigator.geolocation) {
                                    setIsLoadingLocations(true);
                                    navigator.geolocation.getCurrentPosition(
                                        (position) => {
                                            const lat = position.coords.latitude;
                                            const lon = position.coords.longitude;
                                            pendingCurrentLocationRefit.current = true;
                                            setUserSearchedLocation(true);
                                            setGoogleData({
                                                latitude: lat,
                                                longitude: lon,
                                                countryID: 1, stateCode: "", cityState: "",
                                            } as PlaceLocationData);
                                            resolvedLat.current = lat;
                                            resolvedLon.current = lon;

                                            // if user is within ~300m of our cached locationResults,reuse the data, just recenter the map and update address/cookie.
                                            const CURRENT_LOCATION_COORD_THRESHOLD_DEG = 0.003;
                                            const cachedCoords = locationResultsCoordsRef.current;
                                            const cacheHit =
                                                locationResults !== null &&
                                                cachedCoords !== null &&
                                                Math.abs(cachedCoords.lat - lat) < CURRENT_LOCATION_COORD_THRESHOLD_DEG &&
                                                Math.abs(cachedCoords.lon - lon) < CURRENT_LOCATION_COORD_THRESHOLD_DEG;

                                            GetLocationFromLatLon(siteData, lat.toString(), lon.toString(),
                                                (data: any) => {
                                                    const result = data?.data?.data;
                                                    if (result?.city) {
                                                        const city = [result.city, result.stateCode].filter(Boolean).join(", ");
                                                        setAddress(city);
                                                        setRTALocationCookie(createRTALocationCookie({ lat, long: lon, city, state: result.stateCode ?? "" }));
                                                    } else {
                                                        setRTALocationCookie(createRTALocationCookie({ lat, long: lon, city: "", state: "" }));
                                                    }
                                                },
                                                () => {
                                                    setRTALocationCookie(createRTALocationCookie({ lat, long: lon, city: "", state: "" }));
                                                }
                                            );

                                            if (cacheHit) {
                                                setIsLoadingLocations(false);

                                                if (lastServiceRef.current.length > 0) {
                                                    executeSearch(1, false, { lat, lon });
                                                } else {
                                                    setDirectoryResults(null);
                                                }
                                                return;
                                            }

                                            // GPS matches home-baseline but locationResults was overwritten — restore it.
                                            const baseline = homeBaselineRef.current;
                                            const baselineHit =
                                                baseline !== null &&
                                                Math.abs(baseline.lat - lat) < CURRENT_LOCATION_COORD_THRESHOLD_DEG &&
                                                Math.abs(baseline.lon - lon) < CURRENT_LOCATION_COORD_THRESHOLD_DEG;

                                            if (baselineHit && baseline) {
                                                setLocationResults(baseline.data);
                                                locationResultsCoordsRef.current = { lat: baseline.lat, lon: baseline.lon };
                                                setIsLoadingLocations(false);
                                                if (lastServiceRef.current.length > 0) {
                                                    executeSearch(1, false, { lat, lon });
                                                } else {
                                                    setDirectoryResults(null);
                                                }
                                                return;
                                            }

                                            setLocationResults(null);
                                            setAddress("");
                                            fetchLocations(lat, lon, true);
                                            if (lastServiceRef.current.length > 0) {
                                                executeSearch(1, false, { lat, lon });
                                            } else {
                                                setDirectoryResults(null);
                                            }
                                        },
                                        () => {
                                            // Geolocation denied/unavailable — fall back to SF default
                                            const lat = SF_DEFAULT_LAT;
                                            const lon = SF_DEFAULT_LON;
                                            setUserSearchedLocation(true);
                                            setAddress("San Francisco, CA");
                                            setGoogleData({
                                                latitude: lat,
                                                longitude: lon,
                                                countryID: 1, stateCode: "CA", cityState: "San Francisco, CA",
                                            } as PlaceLocationData);
                                            resolvedLat.current = lat;
                                            resolvedLon.current = lon;
                                            setRTALocationCookie(createRTALocationCookie({ lat, long: lon, city: "San Francisco, CA", state: "CA" }));
                                            fetchLocations(lat, lon, false);
                                            if (lastServiceRef.current.length > 0) {
                                                executeSearch(1, false, { lat, lon });
                                            } else {
                                                setDirectoryResults(null);
                                            }
                                            setShowLocationFallbackPopup(true);
                                        }
                                    );
                                }
                            }}
                            userContext={siteData}
                            onSelectAddress={(str: string) => setAddress(str)}
                            initialValue={address}
                            placeHolder={p3}
                            onPlaceSelect={handlePlaceSelect}
                            onBusinessNavigate={(vagaroURL: string) => {
                                const url = getProPageHref(vagaroURL);
                                window.open(url, normalizedLinkTarget);
                            }}
                            initialPromo={configuredPromo}
                        />
                    ) : (
                        <SearchInput
                            defaultValue={searchText}
                            placeHolder={p1}
                            onUpdate={(text) => setSearchText(text)}
                            onClear={() => setSearchText("")}
                        />
                    )}

                    {!hideServices && (
                        <ServiceInputField
                            p2={p2}
                            onFinalize={(text) => {
                                if (lastServiceRef.current === text) return;
                                lastServiceRef.current = text; setLastService(text);
                                if (layout === "2") {
                                    executeSearch(1, false);
                                } else if (singleLocResults !== null && text.length > 0) {
                                    executeSearch(1, false);
                                }
                                if (text.length > 0) handleServiceRestore();
                            }}
                            onSelect={(text) => {
                                if (layout === "2" && text.length > 0) {
                                    lastServiceRef.current = text; setLastService(text);
                                    executeSearch(1, false);
                                } else if (layout !== "2" && text.length > 0 && singleLocResults !== null) {
                                    lastServiceRef.current = text; setLastService(text);
                                    executeSearch(1, false);
                                }
                            }}
                            serviceData={countryFilteredServices}
                            searchText={serviceText}
                            setSearchText={(text) => {
                                setServiceText(text);
                                if (text.length === 0) handleServiceClear();
                                else handleServiceRestore();
                            }}
                        />
                    )}

                    {showPicker && (
                        <div className="dir-picker-wrap" style={{ position: "relative" }}>
                            <button
                                className={"dir-picker-trigger" + (hasDateTimeSelection ? " has-selection" : "")}
                                onClick={() => setPickerOpen(!pickerOpen)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" height="1em" fill="currentColor" viewBox="0 0 448 512"><path d="M152 24c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L64 64C28.7 64 0 92.7 0 128l0 16 0 48L0 448c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-256 0-48 0-16c0-35.3-28.7-64-64-64l-40 0 0-40c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L152 64l0-40zM48 192l352 0 0 256c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256z" /></svg>
                                {formatPickerLabel()}
                            </button>
                            {pickerMounted && (
                                <DateTimePicker
                                    open={pickerOpen}
                                    selectedDate={selectedDate}
                                    selectedTime={selectedTime}
                                    onConfirm={handlePickerConfirm}
                                    onCancel={() => setPickerOpen(false)}
                                />
                            )}
                        </div>
                    )}
                </div>
                <button className="v-button primary dir-filters-btn flex-row gap-med" onClick={() => setDrawerOpen(true)}>
                    <svg xmlns="http://www.w3.org/2000/svg" height="1em" fill="currentColor" viewBox="0 0 512 512"><path d="M463.952 0H48.057C5.419 0-16.094 51.731 14.116 81.941L176 243.882V416c0 15.108 7.113 29.335 19.2 40l64 47.066c31.273 21.855 76.8 1.538 76.8-38.4V243.882L497.893 81.941C528.042 51.792 506.675 0 463.952 0zM288 224v240l-64-48V224L48 48h416L288 224z"/></svg>
                    Filters{activeBTypeFilter.length > 0 ? ` (${activeBTypeFilter.length})` : ""}
                </button>
            </div>
            </div>

            {activeBTypeFilter.length > 0 && (
                <div className="dir-active-filters">
                    {activeBTypeFilter.map(value => (
                        <button key={value} className="dir-active-filter-chip">
                            {value}
                            <span
                                className="dir-chip-remove"
                                onClick={() => handleApplyFilters(activeBTypeFilter.filter(v => v !== value))}
                            >
                                <XIcon />
                            </span>
                        </button>
                    ))}
                </div>
            )}

            <FilterDrawer
                open={drawerOpen}
                categories={filterCategories}
                activeFilters={activeBTypeFilter}
                onApply={handleApplyFilters}
                onClose={() => setDrawerOpen(false)}
            />

            {/* Layout "2": two-panel directory results (cards + map) */}
            {layout === "2" ? (
                <>
                    {/* Show location view until a service search has actually returned results.
                         This prevents the empty state when user is typing a service but hasn't clicked Search yet. */}
                    {/* Skeleton: only shown on very first mount before any data arrives.
                         Once MapPanel mounts it NEVER unmounts — map div stays in DOM permanently. */}
                    {showMapSkeleton ? (
                        <div className="dir-two-panel dir-two-panel-loading">
                            <div className="dir-card-list">
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <div key={index} className="dir-location-card location-box map-box stenciled">
                                        <div className="dir-location-card-header location-header">
                                            <div className="stencil-item data-progress">&nbsp;</div>
                                            <div className="stencil-item data-progress">&nbsp;</div>
                                        </div>
                                        <div className="dir-location-card-address stencil-item data-progress">&nbsp;</div>
                                    </div>
                                ))}
                            </div>
                            <div className="dir-map-container dir-map-loading" />
                        </div>
                    ) : (
                        <RenderDirectoryMapPanel
                            results={crossLocResults?.response ? crossLocResults.response : []}
                            totalResults={crossLocResults?.totalPage}
                            locations={!crossLocResults?.response ? locationGroupsMapped : undefined}
                            onLocationClick={handleLocationCardClick}
                            isSearching={isSearching}
                            isNewSearch={isNewSearch}
                            isLoadingLocations={isLoadingLocations}
                            userLat={resolvedLat.current}
                            userLon={resolvedLon.current}
                            getLocationHref={(location) => getLocationPageHref(location.alias || "")}
                            locationText={googleData?.cityState || ""}
                            onCardClick={(item) => {
                                if (item.mappingURL) {
                                    const url = getProPageHref(item.mappingURL);
                                    window.open(url, normalizedLinkTarget);
                                } else {
                                    showComingSoonToast();
                                }
                            }}
                            onBookClick={(item: SearchListingResult) => {
                                const bookingUrl = buildBookingUrl(item);
                                const wbc = (window as any).wbc;
                                if (wbc?.openVagaroPage) {
                                    wbc.openVagaroPage("divVagaroIframe", bookingUrl, undefined, true);
                                }
                            }}
                            hasMorePages={crossLocResults ? crossLocPage.current < Math.ceil((crossLocResults.totalrecords || crossLocResults.totalPage) / directoryPageSize) : false}
                            onLoadMore={loadMoreCrossLoc}
                            isProResults={(crossLocResults?.response?.length ?? 0) > 0 || activeBTypeFilter.length > 0 || lastService.length > 0}
                            hasDateTimeSelected={hasDateTimeSelected}
                            isUserSearch={userSearchedLocation}
                            searchCenter={{
                                lat: googleData?.latitude ?? resolvedLat.current,
                                lon: googleData?.longitude ?? resolvedLon.current,
                                stateCode: googleData?.stateCode,
                                // State-level search detection: the geocoder leaves `city` empty
                                // when the user searched a state/region (e.g. "Texas" → city: "",
                                // state: "Texas"). City searches always populate `city`.
                                isStateLevelSearch: !!googleData && !googleData.city && !!googleData.stateCode,
                            }}
                            proNameSrc={proNameSrc}
                            proPhotoSrc={proPhotoSrc}
                            enterpriseType={enterpriseType}
                            getProHref={getProPageHref}
                            linkTarget={normalizedLinkTarget}
                            clusterRadius={clusterRadius}
                            onFilterClick={() => setDrawerOpen(true)}
                            sortMode={sortMode}
                            onSortChange={handleSortChange}
                            onClearSearch={handleClearSearch}
                            pendingCurrentLocationRefit={pendingCurrentLocationRefit}
                            onRedoSearchInArea={(lat, lon) => {
                                setUserSearchedLocation(true);
                                GetLocationFromLatLon(siteData, lat.toString(), lon.toString(),
                                    (data: any) => {
                                        const result = data?.data?.data;
                                        const cityName = result?.city
                                            ? [result.city, result.stateCode].filter(Boolean).join(", ")
                                            : "";
                                        const stateCode = result?.stateCode ?? "";
                                        const countryID = result?.countryID ?? siteData.countryCode ?? 1;

                                        resolvedLat.current = lat;
                                        resolvedLon.current = lon;
                                        setGoogleData({
                                            latitude: lat, longitude: lon,
                                            cityState: cityName, stateCode,
                                            countryID,
                                        } as PlaceLocationData);
                                        if (cityName) setAddress(cityName);

                                        executeSearch(1, false, { lat, lon, cityName, stateCode, countryID });
                                    },
                                    () => {
                                        resolvedLat.current = lat;
                                        resolvedLon.current = lon;
                                        executeSearch(1, false, { lat, lon });
                                    }
                                );
                            }}
                        />
                    )}
                </>
            ) : (
                <MobileFilterPill
                    activeCount={activeBTypeFilter.length}
                    onFilterClick={() => setDrawerOpen(true)}
                >
                    <RenderSingleLocResults
                        firstResultList={firstResultListRef.current}
                        results={singleLocResults || []}
                        isSearching={isSearching || singleLocResults === null}
                        locationUnavailable={locationUnavailable}
                        hasDateTimeSelected={hasDateTimeSelected}
                        enterpriseType={enterpriseType}
                        hasMorePages={singleLocTotalPages.current > singleLocPage.current}
                        onLoadMore={loadMoreSingleLoc}
                        getProHref={getProPageHref}
                        linkTarget={normalizedLinkTarget}
                        nameFilter={searchText}
                        proNameSrc={proNameSrc}
                        proPhotoSrc={proPhotoSrc}
                        onClearSearch={handleClearSearch}
                    />
                </MobileFilterPill>
            )}

            {toastVisible && <div className="dir-toast">Coming Soon</div>}

            {showLocationFallbackPopup && (
                <VGPopUp
                    show={true}
                    title="Location Unavailable"
                    onHide={() => setShowLocationFallbackPopup(false)}
                    dialogClassName="vgn-modal-alert"
                    isShowFooter={true}
                    body={
                        <div>
                            <p>We couldn't access your location. Showing results near <strong>San Francisco, CA</strong> instead.</p>
                            <p>Enable location permissions in your browser for more accurate nearby results.</p>
                        </div>
                    }
                    footer={
                        <button onClick={() => setShowLocationFallbackPopup(false)} className="v-button primary invisible-button">OK</button>
                    }
                />
            )}
        </>
    );
}


// --- Single-location server results (Layout 1) ---

/** Renders VagaroSearchBusiness results in the Layout 1 image-centric card style */
function RenderSingleLocResults({ results, isSearching, locationUnavailable, hasDateTimeSelected, enterpriseType, hasMorePages, onLoadMore, getProHref, linkTarget, nameFilter, proNameSrc, proPhotoSrc, firstResultList, onClearSearch }: {
    results: SearchListingResult[];
    isSearching: boolean;
    locationUnavailable?: boolean;
    hasDateTimeSelected: boolean;
    enterpriseType?: string;
    hasMorePages: boolean;
    onLoadMore: () => void;
    getProHref: (mappingURL: string) => string;
    linkTarget: "_self" | "_blank";
    nameFilter: string;
    proNameSrc?: string;
    proPhotoSrc?: string;
    firstResultList?: SearchListingResult[];
    onClearSearch?: () => void;
}) {
    // Server order is preserved — no client-side sorting. Client-side name filter only.
    const sorted = useMemo(() => {
        if (nameFilter.length === 0) return results;
            const filterSplit = nameFilter.toLowerCase().split(" ");
        return results.filter(r => {
                const itemString = `${r.ownerName} ${r.businessName} ${r.facilityValue?.replaceAll(",", " ") ?? ""}`.toLowerCase();
            return filterSplit.every(term => itemString.includes(term));
            });
    }, [results, nameFilter]);

    if (locationUnavailable) {
        return (
            <>
              <div className="rta-empty-state">
                <NoRecordsBanner message="Search at this location is currently unavailable" showInLive={true} />
              </div>
              <div style={{ height: "300px" }} />
            </>
        );
    }

    if (isSearching && sorted.length === 0) {
        return (
            <div className="w-full flex-row flex-wrap" style={{ alignItems: "stretch", paddingBottom: "12px" }}>
                {cardStencilArr.map((item, index) => (
                    <DirectoryProCard key={index} variant="loc-gallery" item={item} isStenciled={true} hasDateTimeSelected={false}  />
                ))}
                          <div className="dir-card-wrapper filler"></div>
                <div className="dir-card-wrapper filler"></div>
                <div className="dir-card-wrapper filler"></div>
                <div className="dir-card-wrapper filler"></div>
                <div className="dir-card-wrapper filler"></div>
                <div className="dir-card-wrapper filler"></div>

            </div>
        );
    }

    <>
    <div className="rta-empty-state">
      <NoRecordsBanner message="Search at this location is currently unavailable" showInLive={true} />
    </div>
    <div style={{ height: "300px" }} />
  </>

    if (sorted.length === 0) {
        return (
            <>
              <div className="rta-empty-state">
                <NoRecordsBanner message="No Results Found" showInLive={true} />
                {onClearSearch && (
                    <div className="rta-empty-state-action">
                        <button className="v-button primary" onClick={onClearSearch}>Clear Search</button>
                    </div>
                )}
            </div>
            <div style={{ height: "300px" }} />
            </>
        );
    }

    return (
        <div className="w-full flex-row flex-wrap" style={{ alignItems: "stretch", paddingBottom: "24px" }}>
            {sorted.map((item, index) => (
                <DirectoryProCard
                    firstResultList={firstResultList}
                    key={(item.merchantId || index) + "-" + index}
                    variant="loc-gallery"
                    item={item}
                    hasDateTimeSelected={hasDateTimeSelected}
                    href={item.mappingURL ? getProHref(item.mappingURL) : "#"}
                    target={linkTarget}
                    proNameSrc={proNameSrc}
                    proPhotoSrc={proPhotoSrc}
                    enterpriseType={enterpriseType}
                />
            ))}
            {isSearching && cardStencilArr.map((item, index) => (
                <DirectoryProCard key={`stencil-${index}`} variant="loc-gallery" item={item} isStenciled={true} hasDateTimeSelected={false} />
            ))}
                <div className="dir-card-wrapper filler"></div>
                <div className="dir-card-wrapper filler"></div>
                <div className="dir-card-wrapper filler"></div>
                <div className="dir-card-wrapper filler"></div>
                <div className="dir-card-wrapper filler"></div>
                <div className="dir-card-wrapper filler"></div>

            {hasMorePages && !isSearching && (
                <LocationScrollSentinel onVisible={onLoadMore} />
            )}
        </div>
    );
}

// --- Map marker icon definitions ---
const MARKER_SVG_PATH = "M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0z";
const DEFAULT_PIN_COLOR = "#4285F4";

/** Darken a hex color by a percentage (0-1). */
function darkenHex(hex: string, amount: number): string {
    const num = parseInt(hex.replace("#", ""), 16);
    if (isNaN(num)) return hex;
    const r = Math.max(0, Math.round(((num >> 16) & 0xFF) * (1 - amount)));
    const g = Math.max(0, Math.round(((num >> 8) & 0xFF) * (1 - amount)));
    const b = Math.max(0, Math.round((num & 0xFF) * (1 - amount)));
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()}`;
}

/** Resolve the map pin color from the --rta-map-pin-color CSS custom property.
 *  Falls back to --theme-color-3, then to DEFAULT_PIN_COLOR. */
function resolveMapPinColor(el?: Element | null): string {
    if (!el) return DEFAULT_PIN_COLOR;
    const raw = getComputedStyle(el).getPropertyValue("--rta-map-pin-color").trim();
    if (raw && raw !== "") return raw;
    // Fallback: read --theme-color-3 from :root
    const themeColor = getComputedStyle(document.documentElement).getPropertyValue("--theme-color-3").trim();
    return themeColor || DEFAULT_PIN_COLOR;
}

/** Custom cluster renderer — reads theme color from CSS variables via resolveMapPinColor.
 *  Matches the pattern in Map.js MyCustomRenderer but uses inherited site styling. */
class ThemedClusterRenderer {
    private mapEl: Element | null;
    constructor(mapEl: Element | null) { this.mapEl = mapEl; }
    render({ count, position }: { count: number; position: unknown }, _stats: unknown) {
        const color = resolveMapPinColor(this.mapEl);
        const svg = window.btoa(`<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240"><circle cx="120" cy="120" r="70" /></svg>`);
        return new (window as WindowWithGoogle).google!.maps!.Marker({
            position,
            icon: {
                url: `data:image/svg+xml;base64,${svg}`,
                scaledSize: new (window as WindowWithGoogle).google!.maps!.Size(75, 75),
            },
            label: {
                text: String(count),
                color: "rgba(255,255,255,1)",
                fontSize: "12px",
            },
            zIndex: Number((window as any).google.maps.Marker.MAX_ZINDEX) + count,
        });
    }
    onAdd() {}
    onRemove() {}
}

const MarkerIcons = {
    default: { path: MARKER_SVG_PATH, fillColor: DEFAULT_PIN_COLOR, fillOpacity: 1, strokeColor: "#FFFFFF", strokeWeight: 1.5, scale: 0.09, anchor: null as unknown, labelOrigin: null as unknown },
    hover: { path: MARKER_SVG_PATH, fillColor: darkenHex(DEFAULT_PIN_COLOR, 0.15), fillOpacity: 1, strokeColor: "#FFFFFF", strokeWeight: 1.5, scale: 0.09, anchor: null as unknown, labelOrigin: null as unknown },
    click: { path: MARKER_SVG_PATH, fillColor: darkenHex(DEFAULT_PIN_COLOR, 0.30), fillOpacity: 1, strokeColor: "#FFFFFF", strokeWeight: 1.5, scale: 0.09, anchor: null as unknown, labelOrigin: null as unknown },
};

/** A grouped Sola location — either derived from SearchListingResult entries or mapped from mysite/distance API */
interface LocationGroup {
    locationName: string;
    street: string;
    stateCity: string;
    zip: string;
    latitude: number;
    longitude: number;
    strDistance: string;
    businessCount: number;
    alias?: string;
    encBID?: string;
}

/** Groups search results by locationName, deduplicating to one entry per physical Sola property.
 *  Uses the first result in each group for address/coordinates. Sorted by distance. */
function groupResultsByLocation(results: SearchListingResult[]): LocationGroup[] {
    const groups: Record<string, LocationGroup> = {};

    for (const item of results) {
        // Use locationName as grouping key; fall back to street address for results without locationName
        const key = item.locationName?.trim() || [item.street, item.stateCity].filter(Boolean).join(", ");
        if (!key) continue;

        if (groups[key]) {
            groups[key].businessCount++;
        } else {
            groups[key] = {
                locationName: item.locationName?.trim() || item.businessName,
                street: item.street || "",
                stateCity: item.stateCity || "",
                zip: item.zip || "",
                latitude: item.latitude,
                longitude: item.longitude,
                strDistance: item.strDistance || "",
                businessCount: 1,
            };
        }
    }

    // Sort by distance (nearest first)
    return Object.values(groups).sort((a, b) => parseDistance(a.strDistance) - parseDistance(b.strDistance));
}

/** Groups pro marker items by physical location (lat/lon), returning one entry per unique location.
 *  Used to prevent stacked pins when multiple pros share a Sola location. */
interface ProLocationGroup {
    latitude: number;
    longitude: number;
    proCount: number;
    firstProIndex: number;
    proIndices: number[];
}

function groupProsByLocation(items: { latitude: number; longitude: number; index: number }[]): ProLocationGroup[] {
    const groups: Record<string, ProLocationGroup> = {};
    for (const item of items) {
        const key = `${item.latitude.toFixed(6)}|${item.longitude.toFixed(6)}`;
        if (groups[key]) {
            groups[key].proCount++;
            groups[key].proIndices.push(item.index);
        } else {
            groups[key] = {
                latitude: item.latitude,
                longitude: item.longitude,
                proCount: 1,
                firstProIndex: item.index,
                proIndices: [item.index],
            };
        }
    }
    return Object.values(groups);
}

type SortMode = "nearest" | "earliest" | "highest-rated" | "ending-soon" | "highest-cost" | "lowest-cost";

function sortModeToSortType(mode: SortMode): number {
    switch (mode) {
        case "nearest": return SortType.Nearest;
        case "earliest": return SortType.Earliest;
        case "highest-rated": return SortType.HighestRated;
        case "ending-soon": return SortType.EndingSoon;
        case "highest-cost": return SortType.HighestCost;
        case "lowest-cost": return SortType.LowestCost;
    }
}

function parseDistance(str: string): number {
    const n = parseFloat(str);
    return isNaN(n) ? Infinity : n;
}

function hasPrice(r: SearchListingResult): boolean {
    return r.price > 0 && r.isShowPriceOnline;
}

function sortDirectoryResults(results: SearchListingResult[], mode: SortMode): SearchListingResult[] {
    if (mode === "lowest-cost" || mode === "highest-cost") {
        // Server sorts by price, but pros with price=0 should be pushed to the bottom.
        return [...results].sort((a, b) => {
            const aHas = hasPrice(a);
            const bHas = hasPrice(b);
            if (aHas !== bHas) return aHas ? -1 : 1;
            if (aHas && bHas) {
                return mode === "lowest-cost" ? a.price - b.price : b.price - a.price;
            }
            return 0;
        });
    }

    return results;
}

/**
 * Computes the zoom level that fits `bounds` within `mapDiv`, accounting for padding.
 * Used to snap directly to the final zoom via setZoom+panTo (no animation) instead of fitBounds.
 */
function computeBoundsZoom(bounds: GoogleMapsLatLngBounds, mapDiv: HTMLElement, padding: number): number {
    const WORLD_PX = 256;
    const ZOOM_MAX = 18;

    function latRad(lat: number): number {
        const sin = Math.sin(lat * Math.PI / 180);
        const clipped = Math.max(Math.min(sin, 1 - 1e-15), -(1 - 1e-15));
        return Math.log((1 + clipped) / (1 - clipped)) / 2;
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const mapH = Math.max(mapDiv.offsetHeight - padding * 2, 1);
    const mapW = Math.max(mapDiv.offsetWidth - padding * 2, 1);
    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;
    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;
    const latZoom = latFraction > 0 ? Math.round(Math.log2(mapH / WORLD_PX / latFraction)) : ZOOM_MAX;
    const lngZoom = lngFraction > 0 ? Math.round(Math.log2(mapW / WORLD_PX / lngFraction)) : ZOOM_MAX;
    return Math.min(latZoom, lngZoom, ZOOM_MAX);
}

/** Two-panel layout: scrollable card list (left) + Google Map (right) */
function RenderDirectoryMapPanel({ results, totalResults, locations, isSearching, isNewSearch, isLoadingLocations, userLat, userLon, locationText, getLocationHref, onCardClick, onBookClick, hasMorePages, onLoadMore, isProResults, hasDateTimeSelected, onLocationClick, isUserSearch, searchCenter, proNameSrc, proPhotoSrc, enterpriseType, getProHref, linkTarget, clusterRadius = 40, onFilterClick, sortMode, onSortChange, onClearSearch, onRedoSearchInArea, pendingCurrentLocationRefit }: {
    isUserSearch?: boolean;
    results: SearchListingResult[];
    totalResults?: number;
    locations?: LocationGroup[];
    isSearching: boolean;
    isNewSearch?: boolean;
    isLoadingLocations?: boolean;
    userLat: number;
    userLon: number;
    locationText: string;
    getLocationHref?: (location: LocationGroup) => string;
    onCardClick: (item: SearchListingResult) => void;
    onBookClick: (item: SearchListingResult) => void;
    hasMorePages: boolean;
    onLoadMore: () => void;
    isProResults: boolean;
    hasDateTimeSelected: boolean;
    onLocationClick?: (location: LocationGroup) => void;
    searchCenter: MapSearchCenter;
    proNameSrc?: string;
    proPhotoSrc?: string;
    enterpriseType?: string;
    getProHref: (mappingURL: string) => string;
    linkTarget: "_self" | "_blank";
    clusterRadius?: number;
    onFilterClick: () => void;
    sortMode: SortMode;
    onSortChange: (mode: SortMode) => void;
    onClearSearch?: () => void;
    onRedoSearchInArea?: (lat: number, lon: number) => void;
    pendingCurrentLocationRefit?: React.MutableRefObject<boolean>;
}) {
    const mapRef = useRef<HTMLDivElement>(null);
    const cardListRef = useRef<HTMLDivElement>(null);
    const googleMapRef = useRef<GoogleMapsMap | null>(null);
    const markersRef = useRef<GoogleMapsMarker[]>([]);
    const markerKeyOrderRef = useRef<string[]>([]);
    const markerSignatureRef = useRef("");
    const clustererRef = useRef<MarkerClusterer | null>(null);
    const lastAppliedEmptyCenterRef = useRef("");

    // Track what searchCenter and mode the last fit was for.
    // transitions (preserve viewport) and on same-center data refreshes.
    const lastFitCenterRef = useRef<{ lat: number; lon: number } | null>(null);
    const lastFitProModeRef = useRef(isProResults);
    const loadMap = useRef(false);
    const hasFirstFitRef = useRef(false);
    const [activeMarkerKey, setActiveMarkerKey] = useState<string | null>(null);
    const [hoverMarkerKey, setHoverMarkerKey] = useState<string | null>(null);
    const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
    const sortDropdownRef = useRef<HTMLDivElement>(null);
    const [showMap, setShowMap] = useState(false);
    const [mapReady, setMapReady] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const [panelVisible, setPanelVisible] = useState(true);

    useEffect(() => {
        const el = panelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => setPanelVisible(entry.isIntersecting),
            { threshold: 0 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Bounds paired with the markerItems they were captured against, so the memo
    // can detect stale pairs during the gap between new data and the next idle.
    type MapBoundsSnapshot = {
        bounds: { north: number; south: number; east: number; west: number };
        items: MarkerDataItem[];
    };
    const [mapBoundsSnapshot, setMapBoundsSnapshot] = useState<MapBoundsSnapshot | null>(null);
    const isProResultsRef = useRef(isProResults);
    isProResultsRef.current = isProResults;
    const markerItemsRef = useRef<MarkerDataItem[]>([]);

    // "Redo Search in This Area" — track whether map has moved from last search center
    const [showRedoSearch, setShowRedoSearch] = useState(false);
    const lastSearchCenterRef = useRef<{ lat: number; lon: number } | null>(null);
    const userDraggedMapRef = useRef(false);
    const programmaticMoveRef = useRef(false);
    // When search center changes (new search completed), sync ref and hide button
    useEffect(() => {
        if (searchCenter) {
            lastSearchCenterRef.current = { lat: searchCenter.lat, lon: searchCenter.lon };
            userDraggedMapRef.current = false;
            setShowRedoSearch(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchCenter.lat, searchCenter.lon]);

    // Fresh search flow can re-use the same searchCenter; clear stale drag intent so
    // the next programmatic idle (fitBounds/panTo) cannot re-show Redo Search Here.
    // Also clear the marker selection — a new search starts with nothing selected.
    useEffect(() => {
        if (isSearching && isNewSearch) {
            userDraggedMapRef.current = false;
            setShowRedoSearch(false);
            setActiveMarkerKey(null);
            markersRef.current.forEach((m) => {
                (m as MarkerWithClickState).__isClicked = false;
                m.setIcon(MarkerIcons.default);
            });
        }
    }, [isSearching, isNewSearch]);

    const sortedResults = useMemo(() => {
        return sortDirectoryResults(results, sortMode);
    }, [results, sortMode]);

    // Use provided locations or fall back to grouping from results
    const locationGroups = useMemo(() => {
        if (isProResults) return [];
        if (locations) return locations;
        return groupResultsByLocation(results);
    }, [results, isProResults, locations]);

    type MarkerDataItem = { key: string; latitude: number; longitude: number; index: number; proCount?: number; proIndices?: number[] };
    const markerItems = useMemo((): MarkerDataItem[] => {
        const seenByBaseKey: Record<string, number> = {};
        const coordinateKey = (lat: number, lon: number) => `${lat.toFixed(6)}|${lon.toFixed(6)}`;
        const uniqueKey = (base: string) => {
            const current = seenByBaseKey[base] ?? 0;
            seenByBaseKey[base] = current + 1;
            return current === 0 ? base : `${base}|dup:${current}`;
        };

        if (isProResults) {
            // Group pros by physical location — one marker per unique lat/lon
            const validPros = sortedResults
                .map((item, index) => ({ latitude: item.latitude, longitude: item.longitude, index }))
                .filter(item => item.latitude && item.longitude);
            const groups = groupProsByLocation(validPros);
            return groups.map((group) => {
                const base = `proloc:${coordinateKey(group.latitude, group.longitude)}`;
                return {
                    key: uniqueKey(base),
                    latitude: group.latitude,
                    longitude: group.longitude,
                    index: group.firstProIndex,
                    proCount: group.proCount,
                    proIndices: group.proIndices,
                };
            });
        }

        return locationGroups
            .map((item, index) => {
                if (!item.latitude || !item.longitude) return null;
                const base = `loc:${item.encBID || item.alias || item.locationName || index}|${coordinateKey(item.latitude, item.longitude)}`;
                return { key: uniqueKey(base), latitude: item.latitude, longitude: item.longitude, index };
            })
            .filter((item): item is MarkerDataItem => item !== null);
    }, [isProResults, sortedResults, locationGroups]);
    markerItemsRef.current = markerItems;

    // null means "show all" — pro mode, pre-first-idle, or snapshot is from older markerItems.
    const locationViewportIndices = useMemo<Set<number> | null>(() => {
        if (isProResults || !mapBoundsSnapshot) return null;
        if (mapBoundsSnapshot.items !== markerItems) return null;
        const { north, south, east, west } = mapBoundsSnapshot.bounds;
        // Google's LatLngBounds wraps the antimeridian when east < west; valid lngs
        // are then [west, 180] ∪ [-180, east]. Naive east/west AND hides every pin.
        const lngInRange = west <= east
            ? (lng: number) => lng >= west && lng <= east
            : (lng: number) => lng >= west || lng <= east;
        const visible = new Set<number>();
        for (const item of markerItems) {
            if (
                item.latitude <= north && item.latitude >= south &&
                lngInRange(item.longitude)
            ) visible.add(item.index);
        }
        return visible;
    }, [markerItems, mapBoundsSnapshot, isProResults]);

    const markerSignature = useMemo(() => {
        if (markerItems.length === 0) return `mode:${isProResults ? "pro" : "loc"}|empty`;
        return `mode:${isProResults ? "pro" : "loc"}|${markerItems.map((item) => `${item.key}@${item.latitude.toFixed(6)},${item.longitude.toFixed(6)}`).join(";")}`;
    }, [isProResults, markerItems]);
    const lastPinColorRef = useRef(DEFAULT_PIN_COLOR);
    const lastClusterRadiusRef = useRef(clusterRadius);
    const clusterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Reverse lookup: cardIndex → markerArrayIndex (handles grouped pros at same location)
    const cardIndexToMarkerIndex = useMemo(() => {
        const lookup: Record<number, number> = {};
        markerItems.forEach((item, markerIdx) => {
            if (item.proIndices) {
                item.proIndices.forEach(ci => { lookup[ci] = markerIdx; });
            } else {
                lookup[item.index] = markerIdx;
            }
        });
        return lookup;
    }, [markerItems]);

    const activeIndex = useMemo(() => {
        if (!activeMarkerKey) return -1;
        const activeItem = markerItems.find((item) => item.key === activeMarkerKey);
        return activeItem ? activeItem.index : -1;
    }, [activeMarkerKey, markerItems]);

    // All pro card indices at the active marker's location (for blue-border highlight).
    // Derived from activeMarkerKey so it can't go stale when a new search replaces markerItems.
    const activeLocationIndices = useMemo(() => {
        const activeItem = activeMarkerKey ? markerItems.find((item) => item.key === activeMarkerKey) : undefined;
        return activeItem?.proIndices && activeItem.proIndices.length > 1
            ? new Set(activeItem.proIndices)
            : new Set<number>();
    }, [activeMarkerKey, markerItems]);

    const hoverIndex = useMemo(() => {
        if (!hoverMarkerKey) return -1;
        const hoverItem = markerItems.find((item) => item.key === hoverMarkerKey);
        return hoverItem ? hoverItem.index : -1;
    }, [hoverMarkerKey, markerItems]);

    const getMarkerKeyForCardIndex = useCallback((cardIndex: number) => {
        const markerIdx = cardIndexToMarkerIndex[cardIndex];
        if (markerIdx === undefined) return null;
        return markerItems[markerIdx]?.key ?? null;
    }, [markerItems, cardIndexToMarkerIndex]);

    const getMarkerArrayIndexForCardIndex = useCallback((cardIndex: number) => {
        return cardIndexToMarkerIndex[cardIndex] ?? -1;
    }, [cardIndexToMarkerIndex]);

    // Close sort dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target as Node)) {
                setSortDropdownOpen(false);
            }
        };
        if (sortDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [sortDropdownOpen]);

    const sortOptions: { value: SortMode; label: string }[] = hasDateTimeSelected
        ? [
            { value: "nearest", label: "Nearest" },
            { value: "earliest", label: "Earliest" },
            { value: "highest-rated", label: "Highest Rated" },
            { value: "highest-cost", label: "Highest Cost" },
            { value: "lowest-cost", label: "Lowest Cost" },
        ]
        : [
            { value: "nearest", label: "Nearest" },
            { value: "highest-rated", label: "Highest Rated" },
        ];

    useEffect(() => {
        if (activeMarkerKey && !markerItems.some((item) => item.key === activeMarkerKey)) {
            setActiveMarkerKey(null);
        }
    }, [activeMarkerKey, markerItems]);

    // Initialize map ONCE — matches RenderBusinessLocation's loadMap guard pattern.
    // The map instance persists across re-renders; fitBounds in the marker effect
    // handles viewport adjustments when data changes.
    useEffect(() => {
        if (loadMap.current || !mapRef.current) return;
        let isCancelled = false;

        const initMap = () => {
            const gmaps = (window as WindowWithGoogle).google?.maps;
            if (!mapRef.current || !gmaps || loadMap.current) return;

            // Initialize anchor/labelOrigin now that google.maps is loaded
            if (!MarkerIcons.default.anchor) {
                MarkerIcons.default.anchor = new gmaps.Point(200, 510);
                MarkerIcons.default.labelOrigin = new gmaps.Point(205, 190);
                MarkerIcons.hover.anchor = new gmaps.Point(200, 510);
                MarkerIcons.hover.labelOrigin = new gmaps.Point(205, 190);
                MarkerIcons.click.anchor = new gmaps.Point(200, 510);
                MarkerIcons.click.labelOrigin = new gmaps.Point(205, 190);
            }

            const isMobileView = window.innerWidth <= 768 || document.body.classList.contains("tablet-break");
            let rtaData = getRTALocationCookie();
            const gmpMap = document.createElement('gmp-map');
            const initCenter = rtaData ? `${rtaData.lat},${rtaData.long}` : '39.8,-98.6';
            const initZoom = rtaData ? 11 : (isMobileView ? 5 : 4);
            gmpMap.setAttribute('center', initCenter);
            gmpMap.setAttribute('zoom', String(initZoom));
            gmpMap.setAttribute('map-id', process.env.REACT_APP_GOOGLE_MAP_API_KEY || '');
            gmpMap.style.width = '100%';
            gmpMap.style.height = '100%';
            mapRef.current.innerHTML = '';
            mapRef.current.appendChild(gmpMap);
            googleMapRef.current = (gmpMap as any).innerMap as GoogleMapsMap;
            googleMapRef.current.setOptions?.({
                mapTypeId: gmaps.MapTypeId.ROADMAP,
                mapTypeControl: false,
                streetViewControl: false,
                maxZoom: 19,
            } as Record<string, unknown>);

            loadMap.current = true;

            // Projection can be null immediately after map creation; wait until it is ready.
            const waitForProjection = () => {
                if (isCancelled) return;
                const hasProjection = (googleMapRef.current as any)?.getProjection?.();
                if (hasProjection) {
                    setMapReady(true);

                    // Viewport filtering: on map idle, compute which markers are within bounds.
                    // Only updates card list in location mode; pro mode uses clustering instead.
                    // Track user-initiated map drags (excludes programmatic fitBounds/panTo)
                    gmaps.event.addListener(googleMapRef.current!, "dragstart", () => {
                        if (programmaticMoveRef.current) return;
                        userDraggedMapRef.current = true;
                    });

                    gmaps.event.addListener(googleMapRef.current!, "idle", () => {
                        programmaticMoveRef.current = false;
                        if (isProResultsRef.current) {
                            // Active search (service and/or filter): offer Redo Search after user drag
                            if (!userDraggedMapRef.current) return;
                            userDraggedMapRef.current = false;
                            const center = googleMapRef.current?.getCenter?.();
                            const last = lastSearchCenterRef.current;
                            if (center && last) {
                                const dlat = Math.abs(center.lat() - last.lat);
                                const dlon = Math.abs(center.lng() - last.lon);
                                if (dlat > REDO_SEARCH_THRESHOLD_DEG || dlon > REDO_SEARCH_THRESHOLD_DEG) {
                                    setShowRedoSearch(true);
                                }
                            }
                            return;
                        }
                        const bounds = googleMapRef.current?.getBounds?.();
                        if (!bounds) return;
                        const ne = bounds.getNorthEast();
                        const sw = bounds.getSouthWest();
                        setMapBoundsSnapshot({
                            bounds: {
                                north: ne.lat(),
                                south: sw.lat(),
                                east: ne.lng(),
                                west: sw.lng(),
                            },
                            items: markerItemsRef.current,
                        });
                    });

                    return;
                }
                window.requestAnimationFrame(waitForProjection);
            };
            waitForProjection();
        };

        Map.loadGoogleMapScript(initMap);
        return () => {
            isCancelled = true;
        };
    }, []);

    const VIEWPORT_FIT_PADDING = 72;
    const ZERO_RESULT_ZOOM = 10;
    const REDO_SEARCH_THRESHOLD_DEG = 0.05; // ~5.5km — pan must be meaningful before showing button

    const applyViewportForMarkers = useCallback((gMap: GoogleMapsMap, gmaps: GoogleMapsNS, items: MarkerDataItem[]) => {
        if (items.length === 0) {
            const fallbackCenter = Number.isFinite(searchCenter?.lat) && Number.isFinite(searchCenter?.lon)
                ? searchCenter
                : { lat: userLat, lon: userLon };
            userDraggedMapRef.current = false;
            programmaticMoveRef.current = true;
            gMap.panTo({ lat: fallbackCenter.lat, lng: fallbackCenter.lon });
            gMap.setZoom(ZERO_RESULT_ZOOM);
            return;
        }

        // Pro results: fit ALL markers so every pin is visible after redo-search.
        // Location results: branch on the user's intent —
        //   - State-level search ("Texas", "Florida"): fit the hardcoded state bbox so the
        //     viewport matches the region, not the geocoder centroid (which often lands in a
        //     sparse subarea and would otherwise zoom into a random nearby town).
        //   - City-level search: fit nearest N markers within ~50mi of the geocoded point.
        if (isUserSearch && Number.isFinite(searchCenter?.lat) && Number.isFinite(searchCenter?.lon)) {
            // State-level fast path: skip nearest-N entirely and fit the state bbox.
            if (!isProResults && searchCenter.isStateLevelSearch && searchCenter.stateCode) {
                const stateBbox = US_STATE_BOUNDS[searchCenter.stateCode];
                if (stateBbox) {
                    const stateBounds = new gmaps.LatLngBounds();
                    stateBounds.extend(new gmaps.LatLng(stateBbox.sw[0], stateBbox.sw[1]));
                    stateBounds.extend(new gmaps.LatLng(stateBbox.ne[0], stateBbox.ne[1]));
                    userDraggedMapRef.current = false;
                    programmaticMoveRef.current = true;
                    gMap.fitBounds(stateBounds, VIEWPORT_FIT_PADDING);
                    return;
                }
                // Unknown stateCode (e.g. future Canada/UK before their bbox tables exist) →
                // fall through to nearest-N logic below.
            }

            const bounds = new gmaps.LatLngBounds();
            bounds.extend(new gmaps.LatLng(searchCenter.lat, searchCenter.lon));

            if (isProResults) {
                items.forEach((item) =>
                    bounds.extend(new gmaps.LatLng(item.latitude, item.longitude))
                );
            } else {
                const MAX_RADIUS_DEG = 0.75; // ~50mi in latitude degrees — matches live behavior
                const NEAREST_FIT = 3;
                const sorted = [...items].sort((a, b) => {
                    const dA = (a.latitude - searchCenter.lat) ** 2 + (a.longitude - searchCenter.lon) ** 2;
                    const dB = (b.latitude - searchCenter.lat) ** 2 + (b.longitude - searchCenter.lon) ** 2;
                    return dA - dB;
                });
                const nearby = sorted
                    .slice(0, NEAREST_FIT)
                    .filter((item) => {
                        const dLat = Math.abs(item.latitude - searchCenter.lat);
                        const dLon = Math.abs(item.longitude - searchCenter.lon);
                        return dLat <= MAX_RADIUS_DEG && dLon <= MAX_RADIUS_DEG;
                    });
                if (nearby.length > 0) {
                    nearby.forEach((item) =>
                        bounds.extend(new gmaps.LatLng(item.latitude, item.longitude))
                    );
                } else {
                    bounds.extend(new gmaps.LatLng(sorted[0].latitude, sorted[0].longitude));
                }
            }

            userDraggedMapRef.current = false;
            programmaticMoveRef.current = true;
            gMap.fitBounds(bounds, VIEWPORT_FIT_PADDING);
            return;
        }

        // Initial load: fit markers to show the populated region, trimming geographic outliers.
        // Without trimming, a single Hawaii / Alaska / PR / international pin pulls the viewport
        // out to the whole hemisphere. Taking the 2nd–98th percentile of lat/lng drops the
        // far-flung outliers while preserving coastal pins (WA/OR/CA/NY/FL all stay in-frame).
        // The MIN_INITIAL_ZOOM floor is the hard guarantee that the viewport never gets wider
        // than CONUS regardless of how the data is distributed.
        // First fit uses setZoom+panTo (instantaneous) to avoid pixelated tile animation on
        // uncached zoom levels. Subsequent fits animate normally via fitBounds.
        const MIN_INITIAL_ZOOM = 4; // floor: never zoom out further than CONUS-level
        const bounds = new gmaps.LatLngBounds();
        if (items.length >= 20) {
            const lats = items.map((i) => i.latitude).sort((a, b) => a - b);
            const lngs = items.map((i) => i.longitude).sort((a, b) => a - b);
            const lo = Math.floor(items.length * 0.02);
            const hi = Math.ceil(items.length * 0.98) - 1;
            bounds.extend(new gmaps.LatLng(lats[lo], lngs[lo]));
            bounds.extend(new gmaps.LatLng(lats[hi], lngs[hi]));
        } else {
            items.forEach((item) => bounds.extend(new gmaps.LatLng(item.latitude, item.longitude)));
        }
        userDraggedMapRef.current = false;
        programmaticMoveRef.current = true;
        if (!hasFirstFitRef.current && mapRef.current) {
            hasFirstFitRef.current = true;
            const zoom = computeBoundsZoom(bounds, mapRef.current, VIEWPORT_FIT_PADDING);
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            gMap.setZoom(Math.max(zoom, MIN_INITIAL_ZOOM));
            gMap.panTo({ lat: (ne.lat() + sw.lat()) / 2, lng: (ne.lng() + sw.lng()) / 2 });
        } else {
            gMap.fitBounds(bounds, VIEWPORT_FIT_PADDING);
        }
    }, [isUserSearch, isProResults, searchCenter, userLat, userLon]);

    // Draw/update markers when marker coordinates change.
    useEffect(() => {
        const gMap = googleMapRef.current;
        const gmaps = (window as WindowWithGoogle).google?.maps;
        if (!gMap || !gmaps || !mapReady) return;

        if (!isProResults && isLoadingLocations && markersRef.current.length > 0) {
            return;
        }

        // Hold viewport + old markers while a new search is in flight; otherwise the
        // empty-items branch of applyViewportForMarkers flashes a zoom-10 jump.
        if (isSearching && isNewSearch && markerItems.length === 0) {
            return;
        }

        const emptyCenterKey = `${(searchCenter?.lat ?? userLat).toFixed(6)}|${(searchCenter?.lon ?? userLon).toFixed(6)}`;

        // Read pin color after paint — CSS variable is guaranteed to be current here
        const pinColor = resolveMapPinColor(mapRef.current);
        const pinColorChanged = pinColor !== lastPinColorRef.current;
        lastPinColorRef.current = pinColor;
        const clusterRadiusChanged = clusterRadius !== lastClusterRadiusRef.current;
        lastClusterRadiusRef.current = clusterRadius;

        if (markerSignatureRef.current === markerSignature && !pinColorChanged && !clusterRadiusChanged) {
            if (markerItems.length === 0 && lastAppliedEmptyCenterRef.current !== emptyCenterKey) {
                applyViewportForMarkers(gMap, gmaps, markerItems);
                lastAppliedEmptyCenterRef.current = emptyCenterKey;
            }
            return;
        }

        // Only cluster radius changed — keep old clusters visible while dragging,
        // then do an atomic teardown + rebuild once the value settles (≈ mouse-up).
        if (markerSignatureRef.current === markerSignature && !pinColorChanged && clusterRadiusChanged) {
            if (clusterDebounceRef.current) clearTimeout(clusterDebounceRef.current);
            clusterDebounceRef.current = setTimeout(() => {
                // Atomic swap: tear down old clusterer and rebuild in one step
                if (clustererRef.current) {
                    clustererRef.current.clearMarkers();
                    clustererRef.current = null;
                    markersRef.current.forEach(m => (m as any).setMap(gMap));
                }
                if (!isProResults && clusterRadius > 0 && markersRef.current.length > 0) {
                    clustererRef.current = new MarkerClusterer({
                        markers: markersRef.current as any[],
                        map: gMap as any,
                        renderer: new ThemedClusterRenderer(mapRef.current),
                        algorithm: new SuperClusterAlgorithm({ radius: clusterRadius, maxZoom: 12 }),
                        onClusterClick: (_event: any, cluster: any, map: any) => {
                            const currentZoom = map.getZoom() ?? 4;
                            map.panTo(cluster.position);
                            map.setZoom(Math.min(currentZoom + 2.5, 16));
                            setActiveMarkerKey(null);
                            markersRef.current.forEach((m) => {
                                (m as MarkerWithClickState).__isClicked = false;
                                m.setIcon(MarkerIcons.default);
                            });
                        },
                    });
                }
            }, 400);
            return;
        }

        MarkerIcons.default.fillColor = pinColor;
        MarkerIcons.hover.fillColor = darkenHex(pinColor, 0.15);
        MarkerIcons.click.fillColor = darkenHex(pinColor, 0.30);

        // Detect append: if new items start with the same keys as existing markers, only add the tail.
        const existingCount = markersRef.current.length;
        const isAppend = existingCount > 0
            && markerItems.length > existingCount
            && !pinColorChanged
            && markerItems.slice(0, existingCount).every((item, i) => item.key === markerKeyOrderRef.current[i]);

        const previousMarkers = !isAppend ? markersRef.current : [];
        const previousClusterer = !isAppend ? clustererRef.current : null;
        if (!isAppend) {
            markersRef.current = [];
            markerKeyOrderRef.current = [];
            clustererRef.current = null;
        }

        const startIndex = isAppend ? existingCount : 0;
        const itemsToAdd = markerItems.slice(startIndex);

        const createMarker = (item: MarkerDataItem) => {
            const pos = new gmaps.LatLng(item.latitude, item.longitude);

            const marker = new gmaps.Marker({
                position: pos,
                icon: MarkerIcons.default,
            });

            if (item.key === activeMarkerKey) {
                marker.setIcon(MarkerIcons.click);
                (marker as MarkerWithClickState).__isClicked = true;
            }

            gmaps.event.addListener(marker, "click", () => {
                const clickedKey = item.key;
                setActiveMarkerKey(clickedKey);
                setHoverMarkerKey(clickedKey);

                // Reset all markers — use ref so closure always sees full set
                markersRef.current.forEach((m, markerIndex) => {
                    const isClicked = markerKeyOrderRef.current[markerIndex] === clickedKey;
                    (m as MarkerWithClickState).__isClicked = isClicked;
                    m.setIcon(isClicked ? MarkerIcons.click : MarkerIcons.default);
                });

                const cardEl = cardListRef.current?.querySelector(`[data-card-index="${item.index}"]`);
                cardEl?.scrollIntoView({ behavior: "smooth", block: "center" });
            });

            gmaps.event.addListener(marker, "mouseover", () => {
                setHoverMarkerKey(item.key);
                if (!(marker as MarkerWithClickState).__isClicked) marker.setIcon(MarkerIcons.hover);
            });

            gmaps.event.addListener(marker, "mouseout", () => {
                setHoverMarkerKey((prev) => (prev === item.key ? null : prev));
                if (!(marker as MarkerWithClickState).__isClicked) marker.setIcon(MarkerIcons.default);
            });

            return marker;
        };

        const addedMarkers = itemsToAdd.map(createMarker);
        markersRef.current = [...markersRef.current, ...addedMarkers];
        markerKeyOrderRef.current = markerItems.map(item => item.key);

        // Add markers to the map — matches Map.js pattern of setting map on creation
        addedMarkers.forEach(m => (m as any).setMap(gMap));

        // Location mode: wrap with MarkerClusterer to group nearby pins (matches RenderBusinessLocation).
        if (!isProResults && clusterRadius > 0) {
            if (clustererRef.current) {
                clustererRef.current.clearMarkers();
                clustererRef.current = null;
            }
            clustererRef.current = new MarkerClusterer({
                markers: markersRef.current as any[],
                map: gMap as any,
                renderer: new ThemedClusterRenderer(mapRef.current),
                // SuperCluster groups by geographic proximity (KD-tree), not screen pixels —
                // gives meaningful clusters at continental zoom where GridAlgorithm collapses
                // everything into one bucket. radius:80 ≈ one cluster per major metro at zoom 4.
                // maxZoom:12 disables clustering at street level so individual pins always appear.
                algorithm: new SuperClusterAlgorithm({ radius: clusterRadius, maxZoom: 12 }),
                onClusterClick: (_event: any, cluster: any, map: any) => {
                    // Keep cluster drill-in smooth: pan toward cluster center and step zoom.
                    // fitBounds + forced zoom can feel like a snap on some map states.
                    const currentZoom = map.getZoom() ?? 4;
                    map.panTo(cluster.position);
                    map.setZoom(Math.min(currentZoom + 2.5, 16));

                    setActiveMarkerKey(null);
                    markersRef.current.forEach((m) => {
                        (m as MarkerWithClickState).__isClicked = false;
                        m.setIcon(MarkerIcons.default);
                    });
                },
            });
        } else if (clustererRef.current) {
            clustererRef.current.clearMarkers();
            clustererRef.current = null;
        }

        if (previousClusterer) {
            previousClusterer.clearMarkers();
        }
        previousMarkers.forEach(m => m.setMap(null));

        markerSignatureRef.current = markerSignature;

        // Fit viewport when:
        //   - Pro mode: any marker change (new search, pagination append, filter re-apply).
        //     The markerSignature early-return upstream prevents redundant runs.
        //   - Location mode: only when searchCenter moves (new search).
        //     Skip pro→location transitions and same-center data refreshes
        //     (e.g. handleApplyFilters revert-refetch) to honor the user's viewport.
        const fitCenter = { lat: searchCenter.lat, lon: searchCenter.lon };
        const centerMoved = !lastFitCenterRef.current
            || lastFitCenterRef.current.lat !== fitCenter.lat
            || lastFitCenterRef.current.lon !== fitCenter.lon;
        const leavingProMode = lastFitProModeRef.current && !isProResults;
        const forceFit = pendingCurrentLocationRefit?.current ?? false;
        if (forceFit && pendingCurrentLocationRefit) pendingCurrentLocationRefit.current = false;
        if (leavingProMode && !forceFit) {
            // Preserve viewport on pro→location transition; Current Location bypasses via forceFit.
            lastFitCenterRef.current = fitCenter;
        } else if (forceFit || isProResults || centerMoved) {
            applyViewportForMarkers(gMap, gmaps, markerItems);
            lastFitCenterRef.current = fitCenter;
        }
        lastFitProModeRef.current = isProResults;
        lastAppliedEmptyCenterRef.current = markerItems.length === 0 ? emptyCenterKey : "";

    // Note: activeMarkerKey intentionally excluded — selection state is handled by setIcon() in
    // click handlers without rebuilding all markers. This prevents flash on every click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markerItems, markerSignature, mapReady, isProResults, isLoadingLocations, isSearching, isNewSearch, locationGroups, applyViewportForMarkers, searchCenter, userLat, userLon, clusterRadius]);

    const handleCardHover = (index: number, entering: boolean) => {
        const markerArrayIndex = getMarkerArrayIndexForCardIndex(index);
        if (markerArrayIndex < 0) return;
        const marker = markersRef.current[markerArrayIndex];
        if (!marker || (marker as MarkerWithClickState).__isClicked) return;
        marker.setIcon(entering ? MarkerIcons.hover : MarkerIcons.default);
    };

    const handleCardClick = (item: SearchListingResult, index: number) => {
        const selectedKey = getMarkerKeyForCardIndex(index);
        setActiveMarkerKey(selectedKey);
        const markerArrayIndex = getMarkerArrayIndexForCardIndex(index);

        // Highlight marker
        markersRef.current.forEach((m, mIdx) => {
            const isClicked = markerItems[mIdx]?.key === selectedKey;
            (m as MarkerWithClickState).__isClicked = isClicked;
            m.setIcon(isClicked ? MarkerIcons.click : MarkerIcons.default);
        });
        const marker = markerArrayIndex >= 0 ? markersRef.current[markerArrayIndex] : null;
        if (marker && selectedKey) {
            marker.setIcon(MarkerIcons.click);
            (marker as MarkerWithClickState).__isClicked = true;
        }
        onCardClick(item);
    };

    const hasResults = isProResults
        ? results && results.length > 0
        : locationGroups.length > 0;

    return (
        <div className={"dir-two-panel" + (showMap ? " show-map" : "")} ref={panelRef}>
            <div className="dir-card-list" ref={cardListRef}>
                {isSearching && isNewSearch ? (
                    <RenderProCardStencils count={6} />
                ) : isLoadingLocations ? (
                    <>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={`loc-stencil-${i}`} className="dir-location-card location-box map-box stenciled">
                                <div className="dir-location-card-header location-header">
                                    <div className="stencil-item data-progress">&nbsp;</div>
                                    <div className="stencil-item data-progress">&nbsp;</div>
                                </div>
                                <div className="dir-location-card-address stencil-item data-progress">&nbsp;</div>
                            </div>
                        ))}
                    </>
                ) : hasResults ? (
                    <>
                        {isProResults && (
                            <div className="dir-result-header">
                                <h6 className="dir-result-header-title">{totalResults ?? sortedResults.length} {(totalResults ?? sortedResults.length) === 1 ? "Pro" : "Pros"} available{locationText && <span className="dir-result-location-text"> near {locationText}</span>}</h6>
                                <div className={`dir-sort-dropdown${sortDropdownOpen ? " open" : ""}`} ref={sortDropdownRef}>
                                    <button
                                        type="button"
                                        className="dir-sort-trigger"
                                        onClick={() => setSortDropdownOpen(prev => !prev)}
                                    >
                                        {sortOptions.find(o => o.value === sortMode)?.label}
                                        <svg className="dir-sort-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none">
                                            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                    {sortDropdownOpen && (
                                        <ul className="dir-sort-options">
                                            {sortOptions.map(opt => (
                                                <li
                                                    key={opt.value}
                                                    className={`dir-sort-option${opt.value === sortMode ? " selected" : ""}`}
                                                    onClick={() => { onSortChange(opt.value); setSortDropdownOpen(false); }}
                                                >
                                                    {opt.label}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        )}

                        {isProResults ? (
                            <>
                                {sortedResults.map((item, index) => (
                                    <DirectoryProCard
                                        key={item.merchantId + "-" + index}
                                        variant="directory"
                                        item={item}
                                        href={item.mappingURL ? getProHref(item.mappingURL) : "#"}
                                        target={linkTarget}
                                        index={index}
                                        isActive={activeIndex === index || hoverIndex === index}
                                        isLocationHighlighted={activeLocationIndices.has(index)}
                                        hasDateTimeSelected={hasDateTimeSelected}
                                        onClick={() => handleCardClick(item, index)}
                                        onBookClick={onBookClick}
                                        onMouseEnter={() => handleCardHover(index, true)}
                                        onMouseLeave={() => handleCardHover(index, false)}
                                        proNameSrc={proNameSrc}
                                        proPhotoSrc={proPhotoSrc}
                                        enterpriseType={enterpriseType}

                                    />
                                ))}
                                {isSearching && <RenderProCardStencils count={3} />}
                            </>
                        ) : (() => {
                            const filtered = locationGroups
                                .map((loc, index) => ({ loc, index }))
                                .filter(({ index }) => locationViewportIndices === null || locationViewportIndices.has(index));
                            return filtered.length > 0 ? filtered.map(({ loc, index }) => (
                                    <RenderDirectoryLocationCard
                                        key={loc.locationName + "-" + index}
                                        location={loc}
                                        href={getLocationHref?.(loc) || "#"}
                                        index={index}
                                        isActive={activeIndex === index || hoverIndex === index}
                                        onClick={() => {
                                            setActiveMarkerKey(getMarkerKeyForCardIndex(index));
                                            onLocationClick?.(loc);
                                        }}
                                        onMouseEnter={() => handleCardHover(index, true)}
                                        onMouseLeave={() => handleCardHover(index, false)}
                                    />
                                )) : <>
                                <div className="rta-empty-state">
                                    <NoRecordsBanner message="No locations in this area" showInLive={true} />
                                </div>
                                <div style={{ height: "300px" }} />
                                </>;
                        })()}

                        {isProResults && hasMorePages && !isSearching && (
                            <LocationScrollSentinel
                                onVisible={() => onLoadMore()}
                                scrollRootRef={cardListRef}
                            />
                        )}
                    </>
                ) : isSearching ? (
                    <RenderProCardStencils count={6} />
                ) : (
                    <>
                    <div className="rta-empty-state">
                        <NoRecordsBanner message="No Results Found" showInLive={true} />
                        {onClearSearch && (
                            <div className="rta-empty-state-action">
                                <button className="v-button primary" onClick={onClearSearch}>Clear Search</button>
                            </div>
                        )}

                    </div>
                    <div style={{ height: "300px" }} />
                    </>
                )}
            </div>
            {/* Map div is always rendered — never conditionally mounted/unmounted.
                Matches RenderBusinessLocation's pattern of persistent map sibling.
                Inner div is the Google Maps target; outer container owns positioning for overlays. */}
            <div className="dir-map-container">
                <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
                {showRedoSearch && isProResults && (
                    <button
                        className="v-button primary rta-redo-search-btn"
                        onClick={() => {
                            const center = googleMapRef.current?.getCenter?.();
                            if (!center) return;
                            setShowRedoSearch(false);
                            onRedoSearchInArea?.(center.lat(), center.lng());
                        }}
                    >
                        Redo Search Here
                    </button>
                )}
            </div>
            {panelVisible && !(window as any).isEditor && createPortal(
                <div className="rta-mobile-pill" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
                    <button
                        className={(showMap ? "list-view-btn" : "map-view-btn") + " v-button primary flex-row"}
                        style={{ gap: "4px" }}
                        onClick={(e) => { e.stopPropagation();
                            const goingToMap = !showMap;
                            setShowMap(goingToMap);
                            if (goingToMap) {
                                window.setTimeout(() => {
                                    const gmaps = (window as WindowWithGoogle).google?.maps;
                                    const gMap = googleMapRef.current;
                                    if (gmaps && gMap) {
                                        gmaps.event.trigger(gMap, "resize");
                                        applyViewportForMarkers(gMap, gmaps, markerItemsRef.current);
                                    }
                                }, 150);
                            }
                        }}
                    >
                        {showMap ? (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" height="1em" fill="currentColor" viewBox="0 0 14 12">
                                    <path d="M0.4375 0.953125C0.4375 0.572266 0.730469 0.25 1.14062 0.25H12.8594C13.2402 0.25 13.5625 0.572266 13.5625 0.953125C13.5625 1.36328 13.2402 1.65625 12.8594 1.65625H1.14062C0.730469 1.65625 0.4375 1.36328 0.4375 0.953125ZM0.4375 5.64062C0.4375 5.25977 0.730469 4.9375 1.14062 4.9375H12.8594C13.2402 4.9375 13.5625 5.25977 13.5625 5.64062C13.5625 6.05078 13.2402 6.34375 12.8594 6.34375H1.14062C0.730469 6.34375 0.4375 6.05078 0.4375 5.64062ZM12.8594 11.0312H1.14062C0.730469 11.0312 0.4375 10.7383 0.4375 10.3281C0.4375 9.94727 0.730469 9.625 1.14062 9.625H12.8594C13.2402 9.625 13.5625 9.94727 13.5625 10.3281C13.5625 10.7383 13.2402 11.0312 12.8594 11.0312Z" />
                                </svg> List
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" height="1em" fill="currentColor" viewBox="0 0 18 14">
                                    <path d="M16.9688 0.8125C17.2031 0.8125 17.4375 1.01758 17.4375 1.31055V11.4473C17.4375 11.7988 17.1445 12.1797 16.8223 12.3262L12.3984 13.8496C12.2227 13.9082 11.959 13.9375 11.8125 13.9375C11.6367 13.9375 11.373 13.9082 11.2266 13.8496L6.1875 12.0625L1.17773 13.9082C1.11914 13.9375 1.06055 13.9375 1.00195 13.9375C0.767578 13.9375 0.5625 13.7617 0.5625 13.4688V3.33203C0.5625 2.98047 0.826172 2.59961 1.14844 2.45312L5.57227 0.929688C5.77734 0.871094 5.98242 0.8125 6.1582 0.8125C6.33398 0.841797 6.59766 0.871094 6.77344 0.929688L11.8125 2.6875L16.793 0.871094C16.8516 0.841797 16.9102 0.8125 16.9688 0.8125ZM7.125 2.54102V10.9199L10.875 12.2383V3.85938L7.125 2.54102ZM1.96875 12.1504L5.68945 10.7441H5.71875V2.36523L1.96875 3.68359V12.1504ZM16.0312 11.0957V2.62891L12.2812 4.03516V12.4141L16.0312 11.0957Z" />
                                </svg> Map
                            </>
                        )}
                    </button>
                    {!showMap && (
                        <button className="filter-view-btn v-button primary flex-row" style={{ gap: "4px" }} onClick={(e) => { e.stopPropagation(); onFilterClick(); }} onTouchEnd={(e) => e.stopPropagation()}>
                            <svg xmlns="http://www.w3.org/2000/svg" height="1em" fill="currentColor" viewBox="0 0 512 512">
                                <path d="M463.952 0H48.057C5.419 0-16.094 51.731 14.116 81.941L176 243.882V416c0 15.108 7.113 29.335 19.2 40l64 47.066c31.273 21.855 76.8 1.538 76.8-38.4V243.882L497.893 81.941C528.042 51.792 506.675 0 463.952 0zM288 224v240l-64-48V224L48 48h416L288 224z" />
                            </svg> Filters
                        </button>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
}

/** Layout 1 filter-only pill — fixed to viewport bottom while the card
 *  section is in view. Uses IntersectionObserver to hide when the wrapper
 *  scrolls out of view (e.g. reaching the footer). Uses position:fixed
 *  instead of sticky to avoid ancestor overflow chain issues. */
function MobileFilterPill({ activeCount, onFilterClick, children }: {
    activeCount: number;
    onFilterClick: () => void;
    children: React.ReactNode;
}) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { threshold: 0 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const isEditor = (window as any).isEditor;

    return (
        <div className="dir-single-loc-wrapper" ref={wrapperRef}>
            {children}
            {isVisible && !isEditor && createPortal(
                <button
                    className="v-button primary rta-mobile-filter-btn flex-row gap-med"
                    onClick={(e) => { e.stopPropagation(); onFilterClick(); }}
                    onTouchEnd={(e) => e.stopPropagation()}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" height="1em" fill="currentColor" viewBox="0 0 512 512">
                        <path d="M463.952 0H48.057C5.419 0-16.094 51.731 14.116 81.941L176 243.882V416c0 15.108 7.113 29.335 19.2 40l64 47.066c31.273 21.855 76.8 1.538 76.8-38.4V243.882L497.893 81.941C528.042 51.792 506.675 0 463.952 0zM288 224v240l-64-48V224L48 48h416L288 224z" />
                    </svg>
                    Filters{activeCount > 0 ? ` (${activeCount})` : ""}
                </button>,
                document.body
            )}
        </div>
    );
}

/** Invisible sentinel element that triggers onVisible when scrolled into view.
 *  Used for progressive location loading — as user scrolls near the bottom, more cards appear and the map zooms out. */
function LocationScrollSentinel({ onVisible, scrollRootRef }: {
    onVisible: () => void;
    scrollRootRef?: React.RefObject<HTMLDivElement>;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const lockRef = useRef(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const root = scrollRootRef?.current ?? null;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting || lockRef.current) return;
                // Only fire if the container is actually scrollable — prevents rapid-fire
                // loading when results don't fill the panel height
                if (root && root.scrollHeight <= root.clientHeight) return;
                lockRef.current = true;
                onVisible();
                window.setTimeout(() => { lockRef.current = false; }, 250);
            },
            {
                root,
                rootMargin: "200px",
                threshold: 0.01,
            }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [onVisible, scrollRootRef]);
    return <div ref={ref} style={{ height: 1 }} />;
}

/** Location card — default view showing grouped Sola locations */
function RenderDirectoryLocationCard({ location, href, index, isActive, onClick, onMouseEnter, onMouseLeave }: {
    location: LocationGroup; href: string; index: number; isActive: boolean; onClick: () => void;
    onMouseEnter: () => void; onMouseLeave: () => void;
}) {
    return (
        <a
            className={"dir-location-card location-box map-box" + (isActive ? " active active-cardbox" : "")}
            href={href}
            data-card-index={index}
            onClick={(event) => {
                const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
                if (!isModifiedClick) {
                    event.preventDefault();
                    onClick();
                }
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="dir-location-card-header location-header">
                <h3 className="dir-location-card-name">{decodeAmp(location.locationName)}</h3>
                {location.strDistance && <p className="dir-location-card-distance">{location.strDistance}</p>}
            </div>
            <div className="dir-location-card-address stencil-item map-label no-a">
                {[location.street, location.stateCity, location.zip].filter(Boolean).join(" ")}
            </div>
        </a>
    );
}

/** Skeleton placeholder cards shown while pro search results are loading */
function RenderProCardStencils({ count }: { count: number }) {
    return <>{Array.from({ length: count }).map((_, i) => (
        <div key={`pro-stencil-${i}`} className="dir-pro-card stenciled">
            <div className="dir-pro-card-photo stencil-item data-progress">&nbsp;</div>
            <div className="dir-pro-card-info">
                <div className="stencil-item data-progress">&nbsp;</div>
                <div className="stencil-item data-progress">&nbsp;</div>
                <div className="stencil-item data-progress">&nbsp;</div>
            </div>
        </div>
    ))}</>;
}

// --- Reused internal components (adapted from RenderBusinessOwnerName) ---

function SearchInput({ defaultValue, placeHolder, onUpdate, onClear }: { defaultValue: string; placeHolder: string; onUpdate: (val: string) => void; onClear: (val: string) => void }) {
    return (
        <div className={"relative w-full ownn-search search-input " + (defaultValue.length <= 0 ? "empty" : "")} style={{ fontSize: "16px" }}>
            <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512" fill="#333" style={{ position: "absolute", left: "8px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z" /></svg>
            <input value={defaultValue} maxLength={100} className="w-full" type="text" style={{ padding: "10px 8px 10px 28px" }} placeholder={placeHolder}
                onInput={(e) => onUpdate((e.target as HTMLInputElement).value)} />
            {defaultValue.length > 0 && (
                <div style={{ left: "inherit", position: "absolute", right: "8px", top: "10px", zIndex: 5 }} onClick={() => onClear("")}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#333" className="bi bi-x-lg" viewBox="0 0 16 16">
                        <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
                    </svg>
                </div>
            )}
        </div>
    );
}

function ServiceInputField({ serviceData, searchText, setSearchText, onSelect, onFinalize, p2 }: {
    p2: string; onFinalize: (text: string) => void; searchText: string; setSearchText: (text: string) => void; serviceData: AdminServiceData[]; onSelect: (text: string) => void;
}) {
    const filtered = getFilteredServiceCatList(serviceData, searchText);
    const options: any[] = [];
    for (let i = 0; i < filtered.length; i++) {
        options.push({ content: <b>{filtered[i].category}</b>, id: filtered[i].category, isHeading: true });
        for (let j = 0; j < filtered[i].service.length; j++) {
            options.push({ content: filtered[i].nodes[j], id: filtered[i].service[j], isHeading: false });
        }
    }

    return (
        <InputWithDropdown
            clsName="service-search-feild"
            loading={serviceData.length <= 0}
            onClear={() => { setSearchText(""); onSelect(""); }}
            onSelect={(id: string) => { setSearchText(id); onSelect(id); }}
            onChange={(text: string) => { setSearchText(text); }}
            onFinalize={(text: string) => { setSearchText(text); onFinalize(text); }}
            defaultValue={searchText}
            placeHolder={p2}
            options={options}
            inputStyle={{ padding: "10px 28px 10px 28px" }}
        />
    );
}

// Default location when browser geolocation is denied or unavailable
const SF_DEFAULT_LAT = 37.7749;
const SF_DEFAULT_LON = -122.4194;

const RTA_LOCATION_COOKIE = "_rta-location";

interface RTALocationCookie {
    lat: number;
    long: number;
    countryid: number | null;
    zip: string;
    city: string;
    state: string;
    currencysymbol: string;
    businesstypes: string;
    service: string;
    serviceDate: string;
    serviceTime: string;
    stateName: string;
    utcOffset: number | null;
    timeZoneOffSet: number | null;
    isSupportDayLight: boolean | null;
}

interface RTALocationCookieParams {
    lat: number;
    long: number;
    city: string;
    state: string;
    countryid?: number | null;
    currencysymbol?: string;
    stateName?: string;
    utcOffset?: number | null;
    timeZoneOffSet?: number | null;
    isSupportDayLight?: boolean | null;
}

function createRTALocationCookie(params: RTALocationCookieParams): RTALocationCookie {
    return {
        lat: params.lat, long: params.long,
        countryid: params.countryid ?? null, zip: "", city: params.city, state: params.state,
        currencysymbol: params.currencysymbol ?? "", businesstypes: "", service: "",
        serviceDate: "", serviceTime: "", stateName: params.stateName ?? "",
        utcOffset: params.utcOffset ?? null, timeZoneOffSet: params.timeZoneOffSet ?? null,
        isSupportDayLight: params.isSupportDayLight ?? null,
    };
}


function getRTALocationCookie(): RTALocationCookie | null {
    const raw = getCookie(GetCookieName(RTA_LOCATION_COOKIE));
    if (!raw) return null;
    try { return JSON.parse(raw); }
    catch { return null; }
}

function setRTALocationCookie(data: RTALocationCookie) {
    setCookie(GetCookieName(RTA_LOCATION_COOKIE), JSON.stringify(data), 30);
}

/** Icons matching LocationFilterDropdown in RenderBusinessLocation */
function RTALocationIcon() {
    return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" height="1em" fill="currentColor"><path d="M429.6 92.1c4.9-11.9 2.1-25.6-7-34.7s-22.8-11.9-34.7-7l-352 144c-14.2 5.8-22.2 20.8-19.3 35.8s16.1 25.8 31.4 25.8H224V432c0 15.3 10.8 28.4 25.8 31.4s30-5.1 35.8-19.3l144-352z" /></svg>;
}
function RTAPinIcon() {
    return <svg xmlns="http://www.w3.org/2000/svg" height="1em" fill="currentColor" viewBox="0 0 384 512"><path d="M192 512s192-208 192-320C384 86 298 0 192 0S0 86 0 192C0 304 192 512 192 512zm0-384a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" /></svg>;
}
function RTABusinessIcon() {
    return <svg xmlns="http://www.w3.org/2000/svg" height="1em" fill="currentColor" viewBox="0 0 384 512"><path d="M48 0C21.5 0 0 21.5 0 48V464c0 26.5 21.5 48 48 48h96V432c0-26.5 21.5-48 48-48s48 21.5 48 48v80h96c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48H48zM64 240c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H80c-8.8 0-16-7.2-16-16V240zm112-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H176c-8.8 0-16-7.2-16-16V240c0-8.8 7.2-16 16-16zm80 16c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H272c-8.8 0-16-7.2-16-16V240zM80 96h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H80c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16zm80 16c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H176c-8.8 0-16-7.2-16-16V112zM272 96h32c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H272c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16z" /></svg>;
}

function LocationSearchInput({ userContext, placeHolder, initialValue, onSelectAddress, onSelectCurrent, onPlaceSelect, onBusinessNavigate, businessList, initialPromo }: {
    initialValue?: string; userContext: UserContext; placeHolder: string;
    onSelectAddress: (val: string) => void; onSelectCurrent: () => void;
    onPlaceSelect?: (placeID: string, label: string) => void;
    onBusinessNavigate?: (vagaroURL: string) => void;
    businessList?: any[] | null;
    initialPromo?: string;
}) {
    const [search, setSearch] = useState(initialValue ?? "");
    const [focused, setFocused] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [locations, setLocations] = useState<{ CityState: string; place_id: string; countryName: string }[]>([]);
    const [businesses, setBusinesses] = useState<{ businessID: string; businessName: string; cityState: string; vagaroURL: string }[]>([]);
    const ref = React.useRef<HTMLDivElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const hasSelected = React.useRef(!!initialValue);
    const confirmedValue = React.useRef(initialValue ?? "");
    const searchRef = React.useRef(search);
    searchRef.current = search;
    const mountedRef = React.useRef(true);
    useEffect(() => { return () => { mountedRef.current = false; }; }, []);
    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const searchRequestIdRef = React.useRef<number>(0);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    // Location for proximity-aware search
    const userLat = useRef(SF_DEFAULT_LAT);
    const userLon = useRef(SF_DEFAULT_LON);
    useEffect(() => {
        const cached = getRTALocationCookie();
        if (cached) { userLat.current = cached.lat; userLon.current = cached.long; return; }
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
                (pos) => { userLat.current = pos.coords.latitude; userLon.current = pos.coords.longitude; },
                () => {}
            );
        }
    }, []);

    // Any path that changes `search`'s meaning must invalidate in-flight + cached state.
    const cancelPendingSearch = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        searchRequestIdRef.current++;
        setLocations([]);
        setBusinesses([]);
        setIsSearching(false);
    };

    // Sync from parent — also invalidates in-flight requests 
    useEffect(() => {
        if (initialValue !== undefined) {
            setSearch(initialValue);
            hasSelected.current = !!initialValue;
            confirmedValue.current = initialValue;
            cancelPendingSearch();
        }
    }, [initialValue]);

    const handleBlur = () => {
        setSearch(confirmedValue.current);
        hasSelected.current = !!confirmedValue.current;
        setFocused(false);
        cancelPendingSearch();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Enter" && e.key !== "ArrowDown" && e.key !== "ArrowUp") {
            setSelectedIndex(-1); return;
        }
        if (!containerRef.current) return;
        e.preventDefault();
        const options = containerRef.current.querySelectorAll(".filter-item:not(.divider)");
        let idx = selectedIndex;
        if (e.key === "ArrowDown") idx++;
        else if (e.key === "ArrowUp") idx--;
        idx = Math.max(0, Math.min(idx, options.length - 1));
        options.forEach((el) => (el as HTMLElement).style.background = "");
        setSelectedIndex(idx);
        const opt = options[idx] as HTMLElement | undefined;
        if (opt) { opt.scrollIntoView({ block: "nearest" }); opt.style.background = "#e0e0e0"; }
        if (e.key === "Enter" && opt) opt.click();
    };

    // Debounced search via SearchLocations (public API)
    const handleInput = (val: string) => {
        setSearch(val);
        setFocused(true);
        setSelectedIndex(-1);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const requestId = ++searchRequestIdRef.current;
        if (val.length >= 3) {
            setIsSearching(true);
            debounceRef.current = setTimeout(() => {
                SearchLocations(userContext, val, userLat.current, userLon.current, 1, initialPromo || "",
                    (data: LocationSearchResult) => {
                        if (!mountedRef.current) return;
                        if (requestId !== searchRequestIdRef.current) return;
                        setLocations(data?.locations ?? []);
                        setBusinesses(data?.businesses ?? []);
                        setIsSearching(false);
                    },
                    () => {
                        if (!mountedRef.current) return;
                        if (requestId !== searchRequestIdRef.current) return;
                        setLocations([]); setBusinesses([]); setIsSearching(false);
                    }
                );
            }, 300);
        } else {
            setLocations([]); setBusinesses([]); setIsSearching(false);
        }
    };

    // Also show matching businesses from the pre-fetched enterprise list (client-side filter)
    const filteredEnterpriseList = useMemo(() => {
        if (!businessList || search.length < 3) return [];
        const terms = search.toLowerCase().split(" ");
        return businessList.filter((item: any) => {
            const full = (item.all || "").toLowerCase();
            return terms.every((t: string) => full.includes(t));
        });
    }, [businessList, search]);

    // Merge: API businesses + enterprise list (deduplicated)
    const allBusinesses = useMemo(() => {
        const apiIds = new Set(businesses.map(b => b.businessID));
        const enterprise = filteredEnterpriseList
            .filter((item: any) => !apiIds.has(item.encBID))
            .map((item: any) => ({ businessID: item.encBID, businessName: item.businessName, cityState: item.city + ", " + item.stateCode, vagaroURL: item.vagaroURL }));
        return [...businesses, ...enterprise];
    }, [businesses, filteredEnterpriseList]);

    return (
        <div className={"relative w-full search-location-input " + (search.length <= 0 ? "empty" : "")} ref={ref} style={{ fontSize: "16px" }}>
            <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512" fill="#333" style={{ position: "absolute", left: "8px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z" /></svg>
            <input
                onFocus={() => {
                    setFocused(true);
                    if (search.length >= 3 && locations.length === 0 && businesses.length === 0 && !isSearching) {
                        handleInput(search);
                    }
                }}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                maxLength={100} className="w-full" type="text"
                style={{ padding: "10px 8px 10px 28px" }}
                placeholder={placeHolder}
                value={search} onChange={(e) => handleInput(e.target.value)} />
            {focused && (
                <div className="location-filter-dropdown" ref={containerRef} onMouseDown={(e) => e.preventDefault()}>
                    {isSearching ? (
                        <div className="filter-item flex-row align-center gap-med">Searching...</div>
                    ) : (
                        <>
                            <div className="filter-item flex-row gap-med" style={{ color: "#0073bb" }}
                                onClick={() => { onSelectCurrent(); hasSelected.current = true; setFocused(false); }}>
                                <RTALocationIcon /><span className="current-location">Current Location</span>
                            </div>

                            {locations.length > 0 && (
                                <>
                                    <div className="filter-item divider flex-row gap-med drop-down-location-row">
                                        <RTAPinIcon /><span className="dropdown-location-name">LOCATIONS</span>
                                    </div>
                                    {locations.map((loc) => (
                                        <div key={loc.place_id} className="filter-item inset"
                                            onClick={() => {
                                                if (onPlaceSelect) onPlaceSelect(loc.place_id, loc.CityState);
                                                setSearch(loc.CityState);
                                                confirmedValue.current = loc.CityState;
                                                onSelectAddress(loc.CityState);
                                                hasSelected.current = true;
                                                setFocused(false);
                                                setLocations([]); setBusinesses([]);
                                            }}>
                                            {loc.CityState}{loc.countryName ? `, ${loc.countryName}` : ""}
                                        </div>
                                    ))}
                                </>
                            )}

                            {/* {allBusinesses.length > 0 && (
                                <>
                                    <div className="filter-item divider flex-row gap-med drop-down-business-row">
                                        <RTABusinessIcon /><span className="dropdown-business-name">BUSINESSES</span>
                                    </div>
                                    {allBusinesses.map((biz) => (
                                        <div key={biz.businessID} className="filter-item inset"
                                            onClick={() => {
                                                if (onBusinessNavigate) onBusinessNavigate(biz.vagaroURL);
                                                confirmedValue.current = biz.businessName;
                                                hasSelected.current = true;
                                                setFocused(false);
                                            }}>
                                            <div className="flex-column gap-med">
                                                <div>{biz.businessName}</div>
                                                <div>{biz.cityState}</div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )} */}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function FilterDrawer({ open, categories, activeFilters, onApply, onClose }: {
    open: boolean;
    categories: FacilityCategory[];
    activeFilters: string[];
    onApply: (selected: string[]) => void;
    onClose: () => void;
}) {
    const [pending, setPending] = useState<string[]>(activeFilters);

    // Sync pending state when drawer opens with current active filters
    useEffect(() => {
        if (open) setPending(activeFilters);
    }, [open, activeFilters]);

    // Lock background scroll — iOS Safari ignores overflow:hidden on body,
    // so we pin the body with position:fixed and restore scroll position on close.
    useEffect(() => {
        if (!open) return;
        const scrollY = window.scrollY;
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = "100%";
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.width = "";
            document.body.style.overflow = "";
            window.scrollTo(0, scrollY);
        };
    }, [open]);

    const toggle = (value: string) => {
        setPending((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
    };

    return createPortal(
        <>
            <div className={"dir-drawer-overlay" + (open ? " open" : "")} onClick={onClose} />
            <div className={"dir-drawer" + (open ? " open" : "")}>
                <div className="dir-drawer-header">
                    <h3>Filters</h3>
                    <button className="dir-drawer-close" onClick={onClose}>&times;</button>
                </div>

                <div className="dir-drawer-body">
                    {categories.map((cat) => (
                        <div key={cat.categoryName + cat.order} className={"dir-drawer-category "+cat.categoryName.toLowerCase().replace(" ","-")}>
                            <div className="dir-drawer-category-header">
                                {cat.header === "Business Type" ? cat.categoryName : cat.header}
                            </div>
                            <div className="dir-drawer-items-grid">
                                {cat.SubFacilities.map((sub) => {
                                    const isChecked = pending.includes(sub.value);
                                    return (
                                        <div key={sub.id + sub.value} className={"dir-drawer-option "+sub.value.toLowerCase().replace(" ","-")} onClick={() => toggle(sub.value)}>
                                            <DirCheckbox checked={isChecked} />
                                            <span>{sub.value}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="dir-drawer-footer">
                    <button className="v-button secondary" onClick={() => { setPending([]); onApply([]); }}>
                        Clear Selection
                    </button>
                    <button className="v-button primary" onClick={() => onApply(pending)}>
                        Apply Filters
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
}

/** Checkbox matching AmbiguousCheckSelect visual from CategorySelector.tsx */
function DirCheckbox({ checked }: { checked: boolean }) {
    return (
        <div className={"dir-check" + (checked ? " checked" : "")}>
            {checked && (
                <svg xmlns="http://www.w3.org/2000/svg" height=".8em" viewBox="0 0 448 512" fill="white">
                    <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" />
                </svg>
            )}
        </div>
    );
}
